import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Bot, Plus, X, RefreshCw, Check, Settings, Trash2, Play, Edit,
  MessageSquare, Zap, Image, Search, Globe, Code, Eye, EyeOff,
  ToggleLeft, ToggleRight, ChevronDown, ChevronUp, Copy, Sparkles,
  Key, Wifi, WifiOff, ChevronRight, Star, DollarSign, Package,
  User, Building, Briefcase, Wrench, ShoppingBag, Headphones,
  TrendingUp, Rocket, Shield, Layout, Maximize, AlignLeft, Sidebar,
  MessageCircle, Send, AlertCircle, Check as CheckIcon, Terminal,
  Users, Activity, Layers, Lock, Unlock, Database, Filter, Rss,
  Cpu, Sliders, Gauge, GitBranch, Bolt, FlaskConical, Network,
  Mic, Volume2, Radio, Music
} from "lucide-react";

const NIM_TIERS = {
  tier1: [
    { id: "nim:deepseek-v4-flash", label: "DeepSeek V4 Flash", params: "~7B", latency: "0.17s ✅", provider: "DeepSeek", tool: true },
    { id: "nim:gpt-oss-20b",       label: "GPT OSS 20B",       params: "20B",  latency: "0.28s ✅", provider: "OpenAI",  tool: true },
    { id: "nim:llama-3.2-3b",      label: "Llama 3.2 3B",      params: "3B",   latency: "0.29s ✅", provider: "Meta",    tool: true },
  ],
  tier2: [
    { id: "nim:deepseek-v4-pro",     label: "DeepSeek V4 Pro",     params: "~7B",  latency: "~0.3s",  provider: "DeepSeek", tool: true },
    { id: "nim:llama-70b",           label: "Llama 3.3 70B",       params: "70B",  latency: "~1s",    provider: "Meta",     tool: true },
    { id: "nim:nemotron-70b",        label: "Nemotron 70B",        params: "70B",  latency: "~1s",    provider: "NVIDIA",   tool: true },
    { id: "nim:nemotron-super-49b",  label: "Nemotron Super 49B",  params: "49B",  latency: "~1s",    provider: "NVIDIA",   tool: true },
    { id: "nim:mistral-large",       label: "Mistral Large",       params: "~56B", latency: "~1.5s",  provider: "Mistral",  tool: true },
    { id: "nim:gpt-oss-120b",        label: "GPT OSS 120B",        params: "120B", latency: "~2s",    provider: "OpenAI",   tool: true },
    { id: "nim:kimi-k2",             label: "Kimi K2",             params: "~32B", latency: "~2s",    provider: "Moonshot", tool: false },
    { id: "nim:llama-4-maverick",    label: "Llama 4 Maverick 17B",params: "17B",  latency: "~3s",    provider: "Meta",     tool: true },
    { id: "nim:mistral-nemo-12b",    label: "Mistral Nemo 12B",    params: "12B",  latency: "~2s",    provider: "Mistral",  tool: true },
    { id: "nim:mistral-small-119b",  label: "Mistral Small 119B",  params: "119B", latency: "~3s",    provider: "Mistral",  tool: true },
    { id: "nim:mistral-medium-128b", label: "Mistral Medium 128B", params: "128B", latency: "~3s",    provider: "Mistral",  tool: true },
    { id: "nim:nemotron-120b",       label: "Nemotron 120B",       params: "120B", latency: "~3s",    provider: "NVIDIA",   tool: true },
    { id: "nim:nemotron-nano-8b",    label: "Nemotron Nano 8B",    params: "8B",   latency: "~2s",    provider: "NVIDIA",   tool: true },
  ],
  tier3: [
    { id: "nim:nemotron-ultra-253b", label: "Nemotron Ultra 253B", params: "253B", latency: "~60s❄",  provider: "NVIDIA",   tool: false },
    { id: "nim:qwen-122b",           label: "Qwen 3.5 122B",       params: "122B", latency: "~30s❄",  provider: "Qwen",     tool: false },
    { id: "nim:qwen-397b",           label: "Qwen 3.5 397B",       params: "397B", latency: "~60s❄",  provider: "Qwen",     tool: false },
    { id: "nim:nemotron-550b",       label: "Nemotron 550B",       params: "550B", latency: "~60s❄",  provider: "NVIDIA",   tool: false },
    { id: "nim:mistral-large-675b",  label: "Mistral Large 675B",  params: "675B", latency: "~90s❄",  provider: "Mistral",  tool: true  },
  ],
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROVIDER_COLORS = {
  Meta: "bg-blue-500/20 text-blue-300",
  Google: "bg-green-500/20 text-green-300",
  Alibaba: "bg-orange-500/20 text-orange-300",
  DeepSeek: "bg-cyan-500/20 text-cyan-300",
  MiniMax: "bg-purple-500/20 text-purple-300",
  Mistral: "bg-pink-500/20 text-pink-300",
  Microsoft: "bg-blue-400/20 text-blue-200",
  NVIDIA: "bg-green-600/20 text-green-200",
  OpenChat: "bg-yellow-500/20 text-yellow-300",
  HuggingFace: "bg-amber-500/20 text-amber-300",
  NousResearch: "bg-violet-500/20 text-violet-300",
  Anthropic: "bg-rose-500/20 text-rose-300",
  OpenAI: "bg-emerald-500/20 text-emerald-300",
};

const CATEGORY_ICONS = {
  usuario: <User size={14} />, profesionales: <Briefcase size={14} />,
  empresas: <Building size={14} />, servicios: <Wrench size={14} />,
  marketplace: <ShoppingBag size={14} />, soporte: <Headphones size={14} />,
  ventas: <TrendingUp size={14} />, onboarding: <Rocket size={14} />,
  super_admin: <Shield size={14} />
};

const FORMAT_ICONS = {
  floating_button: <MessageCircle size={14} />, embedded_window: <Layout size={14} />,
  widget_script: <Code size={14} />, full_page: <Maximize size={14} />,
  sidebar: <AlignLeft size={14} />, inline: <Layers size={14} />
};

// ─── OpenRouter Config Panel ─────────────────────────────────────────────────
function OpenRouterPanel({ status, onStatusRefresh }) {
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const saveKey = async () => {
    if (!apiKey.trim()) { toast.error("Ingresa una API key válida"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/ai-agents/openrouter/configure`, { api_key: apiKey.trim() });
      toast.success("API key guardada correctamente");
      setApiKey("");
      onStatusRefresh();
    } catch (e) {
      toast.error("Error al guardar la API key");
    } finally { setSaving(false); }
  };

  const isConnected = status?.status === "connected";
  const isError = status?.status === "error";

  return (
    <div className="glass-panel p-5 mb-6">
      <div className="flex items-center justify-between cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-4">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
            isConnected ? "bg-green-500/20" : isError ? "bg-red-500/20" : "bg-gray-500/20"
          }`}>
            {isConnected ? <Wifi size={20} className="text-green-400" /> :
             isError ? <WifiOff size={20} className="text-red-400" /> :
             <Key size={20} className="text-gray-400" />}
          </div>
          <div>
            <div className="flex items-center gap-3">
              <span className="text-white font-semibold">OpenRouter API</span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                isConnected ? "bg-green-500/20 text-green-400" :
                isError ? "bg-red-500/20 text-red-400" :
                "bg-gray-500/20 text-gray-400"
              }`}>
                {isConnected ? "Conectado" : isError ? "Error" : "No configurado"}
              </span>
            </div>
            <p className="text-gray-500 text-xs mt-0.5">
              {isConnected
                ? `${status.local_free_models} modelos gratuitos · ${status.paid_models_count} modelos de pago · ${status.api_key_preview}`
                : "Configura tu API key de OpenRouter para habilitar todos los modelos"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {isConnected && (
            <div className="hidden md:flex gap-4 text-xs text-gray-500">
              <span className="flex items-center gap-1"><Unlock size={12} className="text-green-400" />{status.local_free_models} Gratuitos</span>
              <span className="flex items-center gap-1"><Lock size={12} className="text-yellow-400" />{status.paid_models_count} De pago</span>
            </div>
          )}
          {expanded ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
        </div>
      </div>

      {expanded && (
        <div className="mt-5 pt-5 border-t border-white/10">
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Key size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type={showKey ? "text" : "password"}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && saveKey()}
                placeholder="sk-or-v1-xxxxxxxxxxxxxxxxxxxx"
                className="input-glass w-full pl-9 pr-10"
              />
              <button
                onClick={() => setShowKey(!showKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {showKey ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <button onClick={saveKey} disabled={saving} className="btn-primary px-5 flex items-center gap-2">
              {saving ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
              Guardar
            </button>
          </div>
          <p className="text-gray-600 text-xs mt-2">
            Obtén tu API key gratuita en{" "}
            <a href="https://openrouter.ai/keys" target="_blank" rel="noopener noreferrer" className="text-red-400 hover:underline">
              openrouter.ai/keys
            </a>
            {" "}· Los modelos gratuitos no tienen costo adicional
          </p>

          {isConnected && (
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: "Modelos gratuitos", value: status.local_free_models, color: "text-green-400" },
                { label: "Modelos de pago", value: status.paid_models_count, color: "text-yellow-400" },
                { label: "Total en OpenRouter", value: status.total_models, color: "text-blue-400" },
                { label: "Gratuitos en OpenRouter", value: status.free_models_count, color: "text-purple-400" },
              ].map(item => (
                <div key={item.label} className="bg-white/5 rounded-lg p-3">
                  <p className={`text-lg font-bold ${item.color}`}>{item.value}</p>
                  <p className="text-gray-500 text-xs">{item.label}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── TokenRa Config Panel ────────────────────────────────────────────────────
function TokenRaPanel({ status, onStatusRefresh }) {
  const [modelId, setModelId] = useState(status?.model_id || "");
  const [enabled, setEnabled] = useState(!!status?.enabled);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setModelId(status?.model_id || "");
    setEnabled(!!status?.enabled);
  }, [status]);

  const saveConfig = async () => {
    if (enabled && !modelId.trim()) {
      toast.error("Ingresa el ID de un modelo habilitado en TokenRa");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/ai-agents/tokenra/configure`, {
        enabled,
        model_id: modelId.trim(),
      });
      toast.success("Configuración de TokenRa actualizada");
      onStatusRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar TokenRa");
    } finally {
      setSaving(false);
    }
  };

  const isReady = status?.status === "ready";
  const isMissing = status?.status === "not_configured" || status?.status === "missing_model";

  return (
    <div className="glass-panel p-5 mb-6">
      <div className="flex items-center justify-between cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-4">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
            isReady ? "bg-violet-500/20" : isMissing ? "bg-yellow-500/20" : "bg-gray-500/20"
          }`}>
            {isReady
              ? <Network size={20} className="text-violet-400" />
              : <Key size={20} className={isMissing ? "text-yellow-400" : "text-gray-400"} />}
          </div>
          <div>
            <div className="flex items-center gap-3">
              <span className="text-white font-semibold">TokenRa</span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                isReady
                  ? "bg-violet-500/20 text-violet-300"
                  : isMissing
                    ? "bg-yellow-500/20 text-yellow-300"
                    : "bg-gray-500/20 text-gray-400"
              }`}>
                {isReady ? "Activo en Jarvis" : status?.enabled ? "Configuración incompleta" : "Inactivo"}
              </span>
            </div>
            <p className="text-gray-500 text-xs mt-0.5">
              {status?.message || "Proveedor compatible con chat completions"}
            </p>
          </div>
        </div>
        {expanded ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
      </div>

      {expanded && (
        <div className="mt-5 pt-5 border-t border-white/10 space-y-4">
          <div className="rounded-xl border border-blue-500/20 bg-blue-500/8 p-3">
            <p className="text-blue-300 text-xs">
              La clave se lee exclusivamente desde <code className="font-mono">TOKENRA_API_KEY</code> en Replit Secrets.
              Este panel nunca la recibe ni la muestra.
            </p>
          </div>

          <div>
            <label className="block text-gray-400 text-xs mb-1.5 font-medium">ID de modelo habilitado</label>
            <input
              value={modelId}
              onChange={(e) => setModelId(e.target.value)}
              placeholder="Ejemplo: modelo-habilitado-en-tokenra"
              className="input-glass w-full font-mono text-sm"
            />
            <p className="text-gray-600 text-xs mt-2">
              Usa exactamente el valor que TokenRa muestra en <code className="font-mono">YOUR_ENABLED_MODEL_ID</code>.
            </p>
          </div>

          <div
            onClick={() => setEnabled(!enabled)}
            className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer ${
              enabled ? "bg-violet-500/10 border-violet-500/30" : "bg-white/3 border-white/10"
            }`}
          >
            <div>
              <p className="text-white text-sm font-medium">Usar TokenRa primero en Jarvis</p>
              <p className="text-gray-500 text-xs">Si falla o el turno es sensible, continúa con NVIDIA/OpenRouter.</p>
            </div>
            <div className={`w-10 h-5 rounded-full relative transition-colors ${enabled ? "bg-violet-500" : "bg-gray-600"}`}>
              <div className={`w-4 h-4 bg-white rounded-full absolute top-0.5 transition-transform ${enabled ? "translate-x-5" : "translate-x-0.5"}`} />
            </div>
          </div>

          <button onClick={saveConfig} disabled={saving} className="btn-primary px-5 flex items-center gap-2">
            {saving ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
            Guardar TokenRa
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Jev Decision Layer Panel ────────────────────────────────────────────────
function JevPanel({ status, onStatusRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    enabled: true,
    model_routing: true,
    task_routing: true,
    tool_guard: true,
    completion_review: true,
    research_review: false,
  });

  useEffect(() => {
    setConfig({
      enabled: status?.enabled !== false,
      model_routing: status?.features?.model_routing !== false,
      task_routing: status?.features?.task_routing !== false,
      tool_guard: status?.features?.tool_guard !== false,
      completion_review: status?.features?.completion_review !== false,
      research_review: status?.features?.research_review === true,
    });
  }, [status]);

  const toggle = (key) => setConfig(prev => ({ ...prev, [key]: !prev[key] }));
  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/ai-agents/jev/configure`, config);
      toast.success("Capa de decisiones Jev actualizada");
      onStatusRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar Jev");
    } finally {
      setSaving(false);
    }
  };

  const features = [
    ["model_routing", "Selección inteligente de modelos", "Elige entre TokenRa, NVIDIA y OpenRouter."],
    ["task_routing", "Enrutamiento de tareas", "Decide entre respuesta rápida, revisión profunda o división."],
    ["tool_guard", "Protección de herramientas", "Revisa acciones con efectos antes de ejecutar MCP."],
    ["completion_review", "Verificación de completitud", "Evita que Jarvis declare una tarea terminada antes de tiempo."],
    ["research_review", "Validación de investigación", "Revisa si la evidencia pública sustenta la respuesta."],
  ];
  const ready = status?.status === "ready";

  return (
    <div className="glass-panel p-5 mb-6 border border-cyan-500/20">
      <div className="flex items-center justify-between cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-4">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${ready ? "bg-cyan-500/20" : "bg-gray-500/20"}`}>
            <Shield size={20} className={ready ? "text-cyan-400" : "text-gray-400"} />
          </div>
          <div>
            <div className="flex items-center gap-3">
              <span className="text-white font-semibold">Jev Decision Layer</span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                ready ? "bg-cyan-500/20 text-cyan-300" : "bg-gray-500/20 text-gray-400"
              }`}>
                {ready ? "Activo en todo OkBot" : status?.configured ? "Inactivo" : "Sin configurar"}
              </span>
            </div>
            <p className="text-gray-500 text-xs mt-0.5">
              {status?.message || "Enrutamiento, seguridad y verificación para Jarvis"}
            </p>
          </div>
        </div>
        {expanded ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
      </div>

      {expanded && (
        <div className="mt-5 pt-5 border-t border-white/10 space-y-4">
          <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/8 p-3">
            <p className="text-cyan-200 text-xs">
              La credencial se lee únicamente desde <code className="font-mono">JEV_API_KEY</code>.
              Jev recibe resúmenes sanitizados, nunca claves, memoria privada ni identificadores personales.
            </p>
          </div>

          <div onClick={() => toggle("enabled")} className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer ${
            config.enabled ? "bg-cyan-500/10 border-cyan-500/30" : "bg-white/3 border-white/10"
          }`}>
            <div>
              <p className="text-white text-sm font-medium">Activar Jev en Jarvis y MCP</p>
              <p className="text-gray-500 text-xs">Si Jev falla, OkBot conserva sus cascadas y políticas locales.</p>
            </div>
            <div className={`w-10 h-5 rounded-full relative ${config.enabled ? "bg-cyan-500" : "bg-gray-600"}`}>
              <div className={`w-4 h-4 bg-white rounded-full absolute top-0.5 transition-transform ${config.enabled ? "translate-x-5" : "translate-x-0.5"}`} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {features.map(([key, title, description]) => (
              <div key={key} onClick={() => toggle(key)} className={`p-3 rounded-xl border cursor-pointer ${
                config[key] ? "bg-white/5 border-cyan-500/25" : "bg-white/2 border-white/8 opacity-60"
              }`}>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-white text-xs font-medium">{title}</p>
                    <p className="text-gray-500 text-[11px] mt-0.5">{description}</p>
                  </div>
                  {config[key] ? <ToggleRight size={22} className="text-cyan-400 flex-shrink-0" /> : <ToggleLeft size={22} className="text-gray-500 flex-shrink-0" />}
                </div>
              </div>
            ))}
          </div>

          <button onClick={save} disabled={saving} className="btn-primary px-5 flex items-center gap-2">
            {saving ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
            Guardar Jev
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Combo Model Selector (3-mode) ───────────────────────────────────────────
const COMBO_OPTIONS = [
  {
    id: "free",
    label: "Combo Esencial 🇨🇳",
    icon: <Unlock size={18} />,
    description: "Todos los modelos chinos (Qwen, DeepSeek, MiniMax, Kimi, GLM, Hunyuan, ERNIE, Doubao) — gratuitos y de pago",
    color: "green",
    bg: "from-green-500/20 to-emerald-500/10",
    border: "border-green-500/40",
    text: "text-green-400",
    badge: "Chinos"
  },
  {
    id: "paid",
    label: "Combo Pro",
    icon: <Star size={18} />,
    description: "Modelos premium: Claude, GPT-4o, Gemini Pro + chinos de pago",
    color: "yellow",
    bg: "from-yellow-500/20 to-amber-500/10",
    border: "border-yellow-500/40",
    text: "text-yellow-400",
    badge: "Pro"
  },
  {
    id: "super",
    label: "Super Combo ⚡ Poderosos",
    icon: <Sparkles size={18} />,
    description: "Todos los modelos más poderosos: Claude Opus 4, GPT-4o, o1 Pro, o3, Gemini 2.5 Pro, Grok 3, Llama 405B, Mistral Large",
    color: "red",
    bg: "from-red-500/20 to-purple-500/10",
    border: "border-red-500/40",
    text: "text-red-400",
    badge: "Poderosos"
  }
];

function ComboModelSelector({ value, onChange, freeModels, paidModels }) {
  const current = value || "free";
  return (
    <div className="space-y-3">
      <p className="text-gray-400 text-sm font-medium mb-1">Modo de Modelos IA</p>
      {COMBO_OPTIONS.map(opt => {
        const selected = current === opt.id;
        return (
          <div
            key={opt.id}
            onClick={() => onChange(opt.id)}
            className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-all ${
              selected
                ? `bg-gradient-to-r ${opt.bg} ${opt.border}`
                : "bg-white/3 border-white/8 hover:bg-white/6"
            }`}
          >
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
              selected ? `bg-gradient-to-br ${opt.bg} ${opt.text}` : "bg-white/10 text-gray-400"
            }`}>
              {opt.icon}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className={`text-sm font-semibold ${selected ? opt.text : "text-white"}`}>{opt.label}</p>
                <span className={`text-xs px-2 py-0.5 rounded-full ${selected ? `bg-${opt.color}-500/20 ${opt.text}` : "bg-white/10 text-gray-400"}`}>{opt.badge}</span>
              </div>
              <p className="text-gray-500 text-xs mt-0.5">{opt.description}</p>
              {selected && (
                <div className="flex gap-3 mt-2 text-xs">
                  {opt.id === "free" && <span className="text-green-400">⚡ {freeModels.length} modelos disponibles</span>}
                  {opt.id === "paid" && <span className="text-yellow-400">💎 {paidModels.length} modelos premium</span>}
                  {opt.id === "super" && (
                    <>
                      <span className="text-green-400">⚡ {freeModels.length} esenciales</span>
                      <span className="text-yellow-400">💎 {paidModels.length} premium</span>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
              selected ? `${opt.border} ${opt.text}` : "border-gray-600"
            }`}>
              {selected && <Check size={12} />}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ModelSelector({ freeModels, paidModels, selectedModels, useAll, onToggleAll, onToggleModel }) {
  const [activeTab, setActiveTab] = useState("free");
  const [providerFilter, setProviderFilter] = useState("all");

  const providers = [...new Set(freeModels.map(m => m.provider))];
  const filtered = activeTab === "free"
    ? (providerFilter === "all" ? freeModels : freeModels.filter(m => m.provider === providerFilter))
    : paidModels;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <span className="text-gray-400 text-sm font-medium">Modelos de IA</span>
        <div className="flex items-center gap-2 cursor-pointer" onClick={onToggleAll}>
          <div className={`w-10 h-5 rounded-full relative transition-colors ${useAll ? "bg-red-500" : "bg-gray-600"}`}>
            <div className={`w-4 h-4 bg-white rounded-full absolute top-0.5 transition-transform ${useAll ? "translate-x-5" : "translate-x-0.5"}`} />
          </div>
          <span className="text-gray-300 text-sm">Combo Inteligente (todos)</span>
        </div>
      </div>

      {useAll ? (
        <div className="bg-gradient-to-r from-red-500/10 to-purple-500/10 border border-red-500/20 rounded-xl p-4 text-center">
          <Sparkles size={20} className="text-red-400 mx-auto mb-2" />
          <p className="text-white text-sm font-medium">Modo Combo Activo</p>
          <p className="text-gray-400 text-xs mt-1">
            El agente usará todos los modelos disponibles, eligiendo el más adecuado para cada respuesta
          </p>
          <div className="flex justify-center gap-4 mt-3 text-xs">
            <span className="text-green-400">{freeModels.length} esenciales</span>
            <span className="text-yellow-400">{paidModels.length} premium</span>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex gap-1 mb-3 p-1 bg-white/5 rounded-lg">
            <button
              onClick={() => setActiveTab("free")}
              className={`flex-1 py-1.5 px-3 rounded-md text-xs font-medium transition-colors ${
                activeTab === "free" ? "bg-green-500/20 text-green-400" : "text-gray-400 hover:text-white"
              }`}
            >
              <Unlock size={12} className="inline mr-1" />Esenciales ({freeModels.length})
            </button>
            <button
              onClick={() => setActiveTab("paid")}
              className={`flex-1 py-1.5 px-3 rounded-md text-xs font-medium transition-colors ${
                activeTab === "paid" ? "bg-yellow-500/20 text-yellow-400" : "text-gray-400 hover:text-white"
              }`}
            >
              <Star size={12} className="inline mr-1" />Premium ({paidModels.length})
            </button>
          </div>

          {activeTab === "free" && (
            <div className="flex flex-wrap gap-1 mb-3">
              <button
                onClick={() => setProviderFilter("all")}
                className={`px-2 py-0.5 rounded-full text-xs ${providerFilter === "all" ? "bg-red-500 text-white" : "bg-white/5 text-gray-400"}`}
              >
                Todos
              </button>
              {providers.map(p => (
                <button
                  key={p}
                  onClick={() => setProviderFilter(p)}
                  className={`px-2 py-0.5 rounded-full text-xs ${providerFilter === p ? "bg-red-500 text-white" : "bg-white/5 text-gray-400"}`}
                >
                  {p}
                </button>
              ))}
            </div>
          )}

          <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
            {filtered.map(model => {
              const selected = selectedModels.includes(model.id);
              return (
                <div
                  key={model.id}
                  onClick={() => onToggleModel(model.id)}
                  className={`flex items-center justify-between p-2.5 rounded-lg cursor-pointer transition-colors border ${
                    selected
                      ? "bg-red-500/15 border-red-500/40"
                      : "bg-white/3 border-white/5 hover:bg-white/8"
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <div className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 ${selected ? "bg-red-500" : "border border-gray-600"}`}>
                      {selected && <Check size={10} className="text-white" />}
                    </div>
                    <div className="min-w-0">
                      <p className="text-white text-xs font-medium truncate">{model.name}</p>
                      <p className="text-gray-600 text-xs truncate">{model.id}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                    {model.context && (
                      <span className="text-gray-500 text-xs">{model.context}</span>
                    )}
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${PROVIDER_COLORS[model.provider] || "bg-white/10 text-gray-400"}`}>
                      {model.provider}
                    </span>
                    {activeTab === "paid" && model.price && (
                      <span className="text-yellow-400 text-xs">{model.price}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {selectedModels.length > 0 && (
            <p className="text-gray-500 text-xs mt-2">
              {selectedModels.length} modelo{selectedModels.length !== 1 ? "s" : ""} seleccionado{selectedModels.length !== 1 ? "s" : ""}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── RapidAPI Picker (for agents) ─────────────────────────────────────────────
const RAPID_CAT_META = {
  musica:          { label: "Música",           emoji: "🎵" },
  entretenimiento: { label: "Entretenimiento",  emoji: "🎬" },
  comida:          { label: "Comida & Bebidas", emoji: "🍕" },
  salud:           { label: "Salud",            emoji: "💊" },
  finanzas:        { label: "Finanzas",         emoji: "💹" },
  viajes:          { label: "Viajes & Clima",   emoji: "✈️" },
  tecnologia:      { label: "Tecnología",       emoji: "💻" },
  ecommerce:       { label: "E-Commerce",       emoji: "🛒" },
};

function RapidAPIsPicker({ selected, onChange }) {
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");

  useEffect(() => {
    axios.get(`${API}/admin/rapidapi/for-agents`)
      .then(r => { setCatalog(r.data.apis || []); setLoading(false); })
      .catch(() => { toast.error("Error al cargar RapidAPI"); setLoading(false); });
  }, []);

  const categories = ["all", ...Array.from(new Set(catalog.map(a => a.category)))];
  const filtered = activeCategory === "all" ? catalog : catalog.filter(a => a.category === activeCategory);

  const toggle = (id) => {
    if (selected.includes(id)) onChange(selected.filter(s => s !== id));
    else onChange([...selected, id]);
  };

  if (loading) return <div className="flex items-center justify-center py-12"><RefreshCw size={20} className="animate-spin text-gray-500" /></div>;

  if (catalog.length === 0) {
    return (
      <div className="text-center py-10 space-y-3">
        <Zap size={36} className="text-yellow-400/40 mx-auto" />
        <p className="text-gray-400 text-sm font-medium">No hay APIs de RapidAPI activas</p>
        <p className="text-gray-600 text-xs">Activa APIs en el panel RapidAPI Hub del administrador y asegúrate de que tengas una clave configurada.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <p className="text-gray-400 text-sm mb-1">RapidAPI Hub — Fuentes Externas</p>
        <p className="text-gray-600 text-xs">
          Vincula APIs de RapidAPI para que este agente las use como fuentes adicionales de conocimiento y datos en tiempo real.
        </p>
      </div>

      <div className="flex flex-wrap gap-1.5 items-center">
        {categories.map(cat => {
          const meta = RAPID_CAT_META[cat];
          return (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                activeCategory === cat
                  ? "bg-yellow-500/20 text-yellow-300 border border-yellow-500/30"
                  : "bg-white/5 text-gray-400 hover:bg-white/10"
              }`}
            >
              {cat === "all" ? "Todas" : `${meta?.emoji || ""} ${meta?.label || cat}`}
            </button>
          );
        })}
        <button
          onClick={() => {
            const allIds = catalog.map(a => a.id);
            const allSel = allIds.every(id => selected.includes(id));
            onChange(allSel ? [] : allIds);
          }}
          className="ml-auto px-2.5 py-1 rounded-lg text-xs font-medium bg-yellow-500/15 text-yellow-300 border border-yellow-500/30 hover:bg-yellow-500/25 transition-colors"
        >
          {catalog.every(a => selected.includes(a.id)) ? "Deseleccionar Todas" : "Seleccionar Todas"}
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1">
        {filtered.map(api => {
          const isSelected = selected.includes(api.id);
          const meta = RAPID_CAT_META[api.category] || {};
          return (
            <div
              key={api.id}
              onClick={() => toggle(api.id)}
              className={`flex items-start gap-2.5 p-3 rounded-xl border cursor-pointer transition-all ${
                isSelected ? "bg-yellow-500/15 border-yellow-500/40" : "bg-white/3 border-white/5 hover:bg-white/8"
              }`}
            >
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-base mt-0.5 ${isSelected ? "bg-yellow-500/30" : "bg-white/10"}`}>
                {api.icon || meta.emoji || "⚡"}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <p className="text-white text-xs font-medium truncate">{api.name}</p>
                  <span className="text-yellow-400 text-[9px] bg-yellow-500/10 px-1 py-0.5 rounded capitalize">{api.plan}</span>
                </div>
                <p className="text-gray-500 text-[10px] truncate">{api.description}</p>
              </div>
              {isSelected && <Check size={12} className="text-yellow-400 flex-shrink-0 ml-auto mt-1" />}
            </div>
          );
        })}
      </div>

      {selected.length > 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-3">
          <p className="text-yellow-300 text-xs font-medium">
            {selected.length} API{selected.length !== 1 ? "s" : ""} de RapidAPI enlazada{selected.length !== 1 ? "s" : ""}
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Knowledge APIs Picker ────────────────────────────────────────────────────
const CATEGORY_LABELS = {
  images: "Imágenes", imagenes: "Imágenes", music: "Música", musica: "Música",
  entertainment: "Entretenimiento", food: "Comida", games: "Juegos",
  health: "Salud", salud: "Salud", finance: "Finanzas", finanzas: "Finanzas",
  travel: "Viajes", viajes: "Viajes", tech: "Tecnología", ecommerce: "E-Commerce",
  general: "General", noticias: "Noticias", conocimiento: "Conocimiento"
};

const CATEGORY_EMOJI = {
  images: "🖼️", imagenes: "🖼️", music: "🎵", musica: "🎵",
  entertainment: "🎬", food: "🍕", games: "🎮", health: "💊", salud: "💊",
  finance: "💹", finanzas: "💹", travel: "✈️", viajes: "✈️",
  tech: "💻", ecommerce: "🛒", general: "🌐", noticias: "📰",
  conocimiento: "📚"
};

function KnowledgeAPIsPicker({ selected, onChange }) {
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");

  useEffect(() => {
    axios.get(`${API}/admin/knowledge-apis/for-agents`)
      .then(r => { setCatalog(r.data.apis || []); setLoading(false); })
      .catch(() => { toast.error("Error al cargar APIs"); setLoading(false); });
  }, []);

  const categories = ["all", ...Array.from(new Set(catalog.map(a => a.category)))];

  const filtered = activeCategory === "all"
    ? catalog
    : catalog.filter(a => a.category === activeCategory);

  const toggle = (id) => {
    if (selected.includes(id)) onChange(selected.filter(s => s !== id));
    else onChange([...selected, id]);
  };

  if (loading) return <div className="flex items-center justify-center py-12"><RefreshCw size={20} className="animate-spin text-gray-500" /></div>;

  return (
    <div className="space-y-4">
      <div>
        <p className="text-gray-400 text-sm mb-1">Bases de Conocimiento Externas</p>
        <p className="text-gray-600 text-xs">
          Selecciona las APIs que este agente usará como fuentes de conocimiento externas. Se inyectarán como contexto en sus conversaciones.
        </p>
      </div>

      {/* Category filter + Select All */}
      <div className="flex flex-wrap gap-1.5 items-center">
        {categories.map(cat => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
              activeCategory === cat
                ? "bg-purple-500/20 text-purple-300 border border-purple-500/30"
                : "bg-white/5 text-gray-400 hover:bg-white/10"
            }`}
          >
            {cat === "all" ? "Todas" : CATEGORY_LABELS[cat] || cat}
          </button>
        ))}
        <button
          onClick={() => {
            const allIds = catalog.map(a => a.id);
            const allSelected = allIds.every(id => selected.includes(id));
            onChange(allSelected ? [] : allIds);
          }}
          className="ml-auto px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-500/15 text-blue-300 border border-blue-500/30 hover:bg-blue-500/25 transition-colors"
        >
          {catalog.every(a => selected.includes(a.id)) ? "Deseleccionar Todas" : "Seleccionar Todas"}
        </button>
      </div>

      {/* APIs grid */}
      <div className="grid grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1">
        {filtered.map(api => {
          const isSelected = selected.includes(api.id);
          return (
            <div
              key={api.id}
              onClick={() => toggle(api.id)}
              className={`flex items-start gap-2.5 p-3 rounded-xl border cursor-pointer transition-all ${
                isSelected
                  ? "bg-purple-500/15 border-purple-500/40"
                  : "bg-white/3 border-white/5 hover:bg-white/8"
              }`}
            >
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-base mt-0.5 ${
                isSelected ? "bg-purple-500/30" : "bg-white/10"
              }`}>
                {CATEGORY_EMOJI[api.category] || "🔌"}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <p className="text-white text-xs font-medium truncate">{api.name}</p>
                  {api.is_free && (
                    <span className="text-green-400 text-[9px] bg-green-500/15 px-1 py-0.5 rounded">GRATIS</span>
                  )}
                </div>
                <p className="text-gray-500 text-[10px] truncate">{api.description}</p>
                {!api.configured && !api.is_free && (
                  <p className="text-yellow-500 text-[10px] flex items-center gap-1 mt-0.5">
                    <Key size={8} />Sin key
                  </p>
                )}
              </div>
              {isSelected && (
                <Check size={12} className="text-purple-400 flex-shrink-0 ml-auto mt-1" />
              )}
            </div>
          );
        })}
      </div>

      {selected.length > 0 && (
        <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-3">
          <p className="text-purple-300 text-xs font-medium mb-1.5">
            {selected.length} API{selected.length !== 1 ? "s" : ""} enlazada{selected.length !== 1 ? "s" : ""}
          </p>
          <div className="flex flex-wrap gap-1">
            {selected.map(id => {
              const api = catalog.find(a => a.id === id);
              return (
                <span key={id} className="flex items-center gap-1 bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded-full text-[10px]">
                  {CATEGORY_EMOJI[api?.category] || "🔌"} {api?.name || id}
                  <button onClick={(e) => { e.stopPropagation(); toggle(id); }} className="hover:text-white ml-0.5">
                    <X size={8} />
                  </button>
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Feeds Picker ─────────────────────────────────────────────────────────────
function FeedsPicker({ selected, onChange, autoSelectAll = false }) {
  const [feeds, setFeeds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/feeds/`)
      .then(r => {
        // preset_feeds comes as { category: [{name, url, type},...], ... }
        // Flatten it to a list with category field attached
        const presetObj = r.data.preset_feeds || {};
        const preset = Object.entries(presetObj).flatMap(([cat, catFeeds]) =>
          (catFeeds || []).map(f => ({ ...f, category: cat, id: f.id || f.url }))
        );
        const custom = (r.data.custom_feeds || []).map(f => ({ ...f, id: f.id || f.url }));
        const allFeeds = [...preset, ...custom];
        setFeeds(allFeeds);
        setLoading(false);
        if (autoSelectAll && (!selected || selected.length === 0) && allFeeds.length > 0) {
          onChange(allFeeds.map(f => f.id));
        }
      })
      .catch(() => { setFeeds([]); setLoading(false); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id) => {
    if (selected.includes(id)) onChange(selected.filter(s => s !== id));
    else onChange([...selected, id]);
  };

  const selectAll = () => {
    const allIds = feeds.map(f => f.id);
    const allSelected = allIds.every(id => selected.includes(id));
    onChange(allSelected ? [] : allIds);
  };

  if (loading) return <div className="flex items-center justify-center py-12"><RefreshCw size={20} className="animate-spin text-gray-500" /></div>;

  if (feeds.length === 0) return (
    <div className="text-center py-12 text-gray-500">
      <Rss size={32} className="mx-auto mb-3 opacity-30" />
      <p className="text-sm">No hay feeds configurados.</p>
      <p className="text-xs mt-1">Agrega feeds en el módulo de Feeds del panel admin.</p>
    </div>
  );

  const categories = Array.from(new Set(feeds.map(f => f.category || "general")));

  return (
    <div className="space-y-4">
      <div>
        <p className="text-gray-400 text-sm mb-1">Feeds de Contenido</p>
        <p className="text-gray-600 text-xs">Selecciona los feeds RSS/URL que este agente usará como fuentes de noticias y contenido actualizado.</p>
      </div>
      <div className="flex justify-between items-center">
        <span className="text-gray-500 text-xs">{feeds.length} feeds disponibles · {selected.length} seleccionados</span>
        <button
          onClick={selectAll}
          className="px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-500/15 text-blue-300 border border-blue-500/30 hover:bg-blue-500/25 transition-colors"
        >
          {feeds.map(f => f.id).every(id => selected.includes(id)) ? "Deseleccionar Todos" : "Seleccionar Todos"}
        </button>
      </div>
      <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
        {categories.map(cat => {
          const catFeeds = feeds.filter(f => (f.category || "general") === cat);
          return (
            <div key={cat}>
              <p className="text-gray-500 text-[10px] font-semibold uppercase tracking-wider mb-1.5">{cat}</p>
              <div className="grid grid-cols-1 gap-1.5">
                {catFeeds.map(feed => {
                  const id = feed.id;
                  const isSelected = selected.includes(id);
                  return (
                    <div
                      key={id}
                      onClick={() => toggle(id)}
                      className={`flex items-center gap-2.5 p-2.5 rounded-xl border cursor-pointer transition-all ${
                        isSelected ? "bg-blue-500/15 border-blue-500/40" : "bg-white/3 border-white/5 hover:bg-white/8"
                      }`}
                    >
                      <div className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 ${isSelected ? "bg-blue-500/30" : "bg-white/10"}`}>
                        <Rss size={12} className={isSelected ? "text-blue-300" : "text-gray-500"} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-white text-xs font-medium truncate">{feed.name || feed.url}</p>
                        {feed.url && <p className="text-gray-500 text-[10px] truncate">{feed.url}</p>}
                      </div>
                      {isSelected && <Check size={12} className="text-blue-400 flex-shrink-0" />}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      {selected.length > 0 && (
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-3">
          <p className="text-blue-300 text-xs font-medium">{selected.length} feed{selected.length !== 1 ? "s" : ""} seleccionado{selected.length !== 1 ? "s" : ""}</p>
        </div>
      )}
    </div>
  );
}

// ─── Agent Modal (Create / Edit) ──────────────────────────────────────────────
const AGENT_MODAL_DEFAULTS = {
  name: "", description: "", greeting: "¡Hola! ¿En qué puedo ayudarte?",
  system_prompt: "", category: "usuario", format_type: "floating_button",
  models: [], skills: [], knowledge_apis: [], rapidapi_apis: [], selected_feeds: [],
  use_all_models: false, model_combo: "super", pexels_enabled: true,
  serpapi_enabled: true, super_fast_response: false, is_active: true,
  color: "#ef4444", icon: "bot",
  // Voice / TTS config
  tts_enabled: true,
  tts_provider: "fish_audio",  // fish_audio | elevenlabs | openai_tts | google_tts | browser
  tts_voice_id: "",
  tts_speed: 1.0,
  tts_fish_emotion: "neutral",  // neutral | happy | sad | excited | calm
  tts_fallback: "browser",      // elevenlabs | openai_tts | browser
  tts_auto_narrate: false,      // auto-narrate bot responses
};

// ─── Voice / TTS Configuration Tab ───────────────────────────────────────────
const TTS_PROVIDERS = [
  { id: "fish_audio",   label: "🐟 Fish Audio",    desc: "Clonación de voz ultra-realista · Recomendado",  badge: "⭐ Premium" },
  { id: "elevenlabs",   label: "ElevenLabs",        desc: "Alta calidad, múltiples voces",                  badge: "Pro" },
  { id: "openai_tts",   label: "OpenAI TTS",        desc: "Voces naturales de OpenAI",                      badge: "" },
  { id: "google_tts",   label: "Google TTS",        desc: "Texto a voz de Google Cloud",                    badge: "" },
  { id: "browser",      label: "Navegador (gratis)", desc: "Web Speech API · Sin latencia",                 badge: "Free" },
];

const FISH_EMOTIONS = [
  { id: "neutral",  label: "Neutral",    emoji: "😐" },
  { id: "happy",    label: "Alegre",     emoji: "😊" },
  { id: "calm",     label: "Calmado",    emoji: "😌" },
  { id: "excited",  label: "Animado",    emoji: "🤩" },
  { id: "sad",      label: "Melancólico",emoji: "😔" },
  { id: "serious",  label: "Serio",      emoji: "🧐" },
];

function VoiceTTSTab({ data, set }) {
  const [testing, setTesting] = useState(false);
  const [fishStatus, setFishStatus] = useState(null);
  const [testText, setTestText] = useState("Hola, soy tu asistente Jarvis. ¿En qué puedo ayudarte hoy?");

  useEffect(() => {
    axios.get(`${API}/admin/fish-audio/status`)
      .then(r => setFishStatus(r.data))
      .catch(() => setFishStatus(null));
  }, []);

  const testVoice = async () => {
    if (!testText.trim()) return;
    setTesting(true);
    try {
      const provider = data.tts_provider;
      if (provider === "fish_audio") {
        const res = await fetch(`${API}/voice/fish-tts/stream?text=${encodeURIComponent(testText)}&speed=${data.tts_speed}&emotion=${data.tts_fish_emotion || 'neutral'}${data.tts_voice_id ? `&reference_id=${data.tts_voice_id}` : ''}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        audio.onended = () => URL.revokeObjectURL(url);
        await audio.play();
      } else if (provider === "browser") {
        window.speechSynthesis.cancel();
        const utt = new SpeechSynthesisUtterance(testText);
        utt.rate = data.tts_speed;
        window.speechSynthesis.speak(utt);
      } else {
        const res = await axios.post(`${API}/voice/tts`, { text: testText, provider });
        if (res.data?.audio_url) {
          const audio = new Audio(res.data.audio_url);
          await audio.play();
        }
      }
      toast.success("✓ Audio reproducido");
    } catch (e) {
      toast.error("Error al probar voz: " + (e.message || "desconocido"));
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-violet-500/10 to-blue-500/10 border border-violet-500/20 rounded-xl">
        <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
          <Volume2 size={18} className="text-violet-400" />
        </div>
        <div>
          <p className="text-white font-semibold text-sm">Configuración de Voz para Jarvis</p>
          <p className="text-gray-400 text-xs">Define cómo Jarvis narrates respuestas y lee audiolibros en OkBook</p>
        </div>
        <label className="ml-auto flex items-center gap-2 cursor-pointer flex-shrink-0">
          <span className="text-xs text-gray-400">TTS activo</span>
          <div
            onClick={() => set("tts_enabled", !data.tts_enabled)}
            className={`w-10 h-5 rounded-full transition-colors flex items-center px-0.5 cursor-pointer ${data.tts_enabled ? "bg-violet-500" : "bg-gray-700"}`}
          >
            <div className={`w-4 h-4 rounded-full bg-white transition-transform ${data.tts_enabled ? "translate-x-5" : "translate-x-0"}`} />
          </div>
        </label>
      </div>

      {/* Fish Audio status chip */}
      {fishStatus && (
        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs ${fishStatus.configured ? "bg-emerald-500/10 border border-emerald-500/20 text-emerald-400" : "bg-yellow-500/10 border border-yellow-500/20 text-yellow-400"}`}>
          <Radio size={12} />
          {fishStatus.configured
            ? `🐟 Fish Audio conectado · Balance: ${fishStatus.balance ?? "—"} créditos`
            : "⚠️ Fish Audio no configurado — configura tu API Key en 🐟 Fish Audio tab"}
        </div>
      )}

      {/* Provider selector */}
      <div>
        <label className="block text-gray-400 text-xs mb-2 font-medium">Proveedor de voz principal</label>
        <div className="space-y-2">
          {TTS_PROVIDERS.map(p => (
            <div
              key={p.id}
              onClick={() => set("tts_provider", p.id)}
              className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                data.tts_provider === p.id
                  ? "bg-violet-500/15 border-violet-500/40"
                  : "bg-white/3 border-white/5 hover:bg-white/7"
              }`}
            >
              <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${data.tts_provider === p.id ? "border-violet-400" : "border-gray-600"}`}>
                {data.tts_provider === p.id && <div className="w-2 h-2 rounded-full bg-violet-400" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-xs font-medium">{p.label}</p>
                <p className="text-gray-500 text-[10px]">{p.desc}</p>
              </div>
              {p.badge && (
                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-violet-500/20 text-violet-300 border border-violet-500/30 flex-shrink-0">{p.badge}</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Fish Audio specific — only when fish_audio selected */}
      {data.tts_provider === "fish_audio" && (
        <div className="space-y-4 p-4 bg-violet-500/5 border border-violet-500/15 rounded-xl">
          <p className="text-violet-300 text-xs font-semibold flex items-center gap-1.5"><Mic size={12} /> Opciones Fish Audio</p>

          <div>
            <label className="block text-gray-400 text-xs mb-1.5 font-medium">Voice/Reference ID (opcional)</label>
            <input
              type="text"
              value={data.tts_voice_id || ""}
              onChange={e => set("tts_voice_id", e.target.value)}
              placeholder="ID de voz de Fish Audio (deja vacío para voz por defecto)"
              className="input-glass w-full text-xs"
            />
            <p className="text-gray-600 text-[10px] mt-1">Puedes obtener IDs de voz en fish.audio/voices</p>
          </div>

          <div>
            <label className="block text-gray-400 text-xs mb-2 font-medium">Emoción / Estilo de narración</label>
            <div className="grid grid-cols-3 gap-2">
              {FISH_EMOTIONS.map(em => (
                <button
                  key={em.id}
                  type="button"
                  onClick={() => set("tts_fish_emotion", em.id)}
                  className={`flex items-center gap-2 p-2 rounded-lg border text-xs transition-colors ${
                    (data.tts_fish_emotion || "neutral") === em.id
                      ? "bg-violet-500/20 border-violet-500/40 text-violet-300"
                      : "bg-white/3 border-white/5 text-gray-400 hover:bg-white/7"
                  }`}
                >
                  <span>{em.emoji}</span><span>{em.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Speed control */}
      <div>
        <label className="block text-gray-400 text-xs mb-2 font-medium">
          Velocidad de habla: <span className="text-white font-semibold">{(data.tts_speed || 1.0).toFixed(1)}×</span>
        </label>
        <input
          type="range" min="0.5" max="2.0" step="0.1"
          value={data.tts_speed || 1.0}
          onChange={e => set("tts_speed", parseFloat(e.target.value))}
          className="w-full accent-violet-500"
        />
        <div className="flex justify-between text-[10px] text-gray-600 mt-1">
          <span>0.5× lento</span><span>1.0× normal</span><span>2.0× rápido</span>
        </div>
      </div>

      {/* Fallback provider */}
      {data.tts_provider !== "browser" && (
        <div>
          <label className="block text-gray-400 text-xs mb-1.5 font-medium">Proveedor de respaldo (si falla el principal)</label>
          <select
            value={data.tts_fallback || "browser"}
            onChange={e => set("tts_fallback", e.target.value)}
            className="input-glass w-full text-sm"
          >
            <option value="browser">Navegador (Web Speech API)</option>
            <option value="openai_tts">OpenAI TTS</option>
            <option value="elevenlabs">ElevenLabs</option>
          </select>
        </div>
      )}

      {/* Auto-narrate toggle */}
      <div className="flex items-center justify-between p-3 bg-white/3 border border-white/5 rounded-xl">
        <div>
          <p className="text-white text-xs font-medium">Narración automática de respuestas</p>
          <p className="text-gray-500 text-[10px]">Jarvis leerá cada respuesta automáticamente sin que el usuario pulse play</p>
        </div>
        <div
          onClick={() => set("tts_auto_narrate", !data.tts_auto_narrate)}
          className={`w-10 h-5 rounded-full transition-colors flex items-center px-0.5 cursor-pointer flex-shrink-0 ml-3 ${data.tts_auto_narrate ? "bg-violet-500" : "bg-gray-700"}`}
        >
          <div className={`w-4 h-4 rounded-full bg-white transition-transform ${data.tts_auto_narrate ? "translate-x-5" : "translate-x-0"}`} />
        </div>
      </div>

      {/* Test voice section */}
      <div className="p-4 bg-white/3 border border-white/8 rounded-xl space-y-3">
        <p className="text-gray-300 text-xs font-medium flex items-center gap-1.5"><Music size={12} className="text-violet-400" /> Probar configuración de voz</p>
        <textarea
          value={testText}
          onChange={e => setTestText(e.target.value)}
          rows={2}
          className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white resize-none outline-none focus:border-violet-500/50"
          placeholder="Escribe texto para probar..."
          maxLength={200}
        />
        <button
          type="button"
          onClick={testVoice}
          disabled={testing || !data.tts_enabled}
          className="w-full py-2 bg-violet-500 hover:bg-violet-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-xs font-medium flex items-center justify-center gap-2 transition-colors"
        >
          {testing
            ? <><RefreshCw size={12} className="animate-spin" /> Reproduciendo…</>
            : <><Play size={12} /> Probar voz ({TTS_PROVIDERS.find(p => p.id === data.tts_provider)?.label || data.tts_provider})</>}
        </button>
      </div>

      {/* OkBook synergy callout */}
      <div className="p-3 bg-blue-500/8 border border-blue-500/20 rounded-xl">
        <p className="text-blue-300 text-xs font-medium mb-1">📚 Sinergia con OkBook</p>
        <p className="text-blue-400/70 text-[10px] leading-relaxed">
          Esta configuración de voz se aplica también a la narración de audiolibros en OkBook. Fish Audio con tu Voice ID personal crea una experiencia completamente personalizada — el mismo Jarvis que responde en chat también narra tus libros.
        </p>
      </div>
    </div>
  );
}

function AgentModal({ agent, categories, formats, skills, freeModels, paidModels, onSave, onClose, isNew }) {
  const [tab, setTab] = useState("basic");
  const [data, setData] = useState({ ...AGENT_MODAL_DEFAULTS, ...agent });
  const [saving, setSaving] = useState(false);

  const set = (field, val) => setData(d => ({ ...d, [field]: val }));

  const toggleSkill = (id) => {
    const list = data.skills || [];
    set("skills", list.includes(id) ? list.filter(s => s !== id) : [...list, id]);
  };
  const toggleModel = (id) => {
    const list = data.models || [];
    set("models", list.includes(id) ? list.filter(m => m !== id) : [...list, id]);
  };

  const handleSave = async () => {
    if (!data.name || !data.system_prompt) {
      toast.error("Nombre y System Prompt son requeridos");
      return;
    }
    setSaving(true);
    try { await onSave(data); }
    catch { /* Error ya mostrado por onSave — modal permanece abierto */ }
    finally { setSaving(false); }
  };

  const tabs = [
    { id: "basic", label: "Configuración", icon: <Settings size={14} /> },
    { id: "models", label: "Modelos", icon: <Zap size={14} /> },
    { id: "voice", label: "Voz IA", icon: <Mic size={14} /> },
    { id: "skills", label: "Skills", icon: <Sparkles size={14} /> },
    { id: "knowledge", label: "APIs Conocimiento", icon: <Database size={14} /> },
    { id: "rapidapi", label: "RapidAPI Hub", icon: <Zap size={14} className="text-yellow-400" /> },
    { id: "feeds", label: "Feeds", icon: <Rss size={14} /> },
    { id: "integration", label: "Integración", icon: <Code size={14} /> },
  ];

  // Map combo to use_all_models + models for save
  const modelCombo = data.model_combo || (data.use_all_models ? "super" : "free");
  const setModelCombo = (val) => {
    setData(d => ({
      ...d,
      model_combo: val,
      use_all_models: val === "super",
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4">
      <div className="glass-panel w-full max-w-2xl animate-scale-in flex flex-col" style={{ maxHeight: "92vh" }}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-4 border-b border-white/10 flex-shrink-0">
          <div>
            <h3 className="text-white font-semibold text-lg">
              {isNew ? "Crear Agente IA" : `Editar: ${data.name}`}
            </h3>
            <p className="text-gray-500 text-xs mt-0.5">
              {isNew ? "Configura tu nuevo agente inteligente" : "Actualiza la configuración del agente"}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-2 hover:bg-white/10 rounded-lg">
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-4 pb-0 flex-shrink-0">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                tab === t.id ? "bg-red-500/20 text-red-400 border border-red-500/30" : "text-gray-400 hover:bg-white/5"
              }`}
            >
              {t.icon}{t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {tab === "basic" && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-xs mb-1.5 font-medium">Nombre del Agente *</label>
                  <input
                    type="text" value={data.name}
                    onChange={e => set("name", e.target.value)}
                    placeholder="Ej: Asistente de Ventas"
                    className="input-glass w-full"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-xs mb-1.5 font-medium">Categoría del Sistema</label>
                  <select value={data.category} onChange={e => set("category", e.target.value)} className="input-glass w-full">
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name} — {cat.description}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-gray-400 text-xs mb-1.5 font-medium">Descripción</label>
                <input
                  type="text" value={data.description || ""}
                  onChange={e => set("description", e.target.value)}
                  placeholder="Breve descripción del agente y su función"
                  className="input-glass w-full"
                />
              </div>

              <div>
                <label className="block text-gray-400 text-xs mb-1.5 font-medium">Saludo Inicial</label>
                <input
                  type="text" value={data.greeting}
                  onChange={e => set("greeting", e.target.value)}
                  placeholder="¡Hola! ¿En qué puedo ayudarte?"
                  className="input-glass w-full"
                />
              </div>

              <div>
                <label className="block text-gray-400 text-xs mb-1.5 font-medium">System Prompt *</label>
                <textarea
                  value={data.system_prompt}
                  onChange={e => set("system_prompt", e.target.value)}
                  placeholder="Eres un asistente experto en ventas para OkBot. Tu objetivo es ayudar a los usuarios a..."
                  className="input-glass w-full resize-none"
                  rows={5}
                />
                <p className="text-gray-600 text-xs mt-1">
                  Define la personalidad, función y comportamiento del agente
                </p>
              </div>

              <div>
                <label className="block text-gray-400 text-xs mb-1.5 font-medium">Formato de Despliegue</label>
                <div className="grid grid-cols-2 gap-2">
                  {formats.map(fmt => (
                    <div
                      key={fmt.id}
                      onClick={() => set("format_type", fmt.id)}
                      className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                        data.format_type === fmt.id
                          ? "bg-red-500/15 border-red-500/40"
                          : "bg-white/3 border-white/5 hover:bg-white/8"
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        data.format_type === fmt.id ? "bg-red-500/30 text-red-400" : "bg-white/10 text-gray-400"
                      }`}>
                        {FORMAT_ICONS[fmt.id] || <Code size={14} />}
                      </div>
                      <div>
                        <p className="text-white text-xs font-medium">{fmt.name}</p>
                        <p className="text-gray-500 text-xs">{fmt.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-gray-400 text-xs mb-2 font-medium">Color del Agente</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color" value={data.color || "#ef4444"}
                    onChange={e => set("color", e.target.value)}
                    className="w-10 h-10 rounded-lg cursor-pointer bg-transparent border-0"
                  />
                  <div className="flex gap-2">
                    {["#ef4444", "#8b5cf6", "#3b82f6", "#10b981", "#f59e0b", "#ec4899"].map(c => (
                      <button
                        key={c} onClick={() => set("color", c)}
                        className="w-6 h-6 rounded-full border-2 transition-transform hover:scale-110"
                        style={{ background: c, borderColor: data.color === c ? "white" : "transparent" }}
                      />
                    ))}
                  </div>
                  <span className="text-gray-500 text-xs">{data.color || "#ef4444"}</span>
                </div>
              </div>

              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={data.pexels_enabled}
                    onChange={e => set("pexels_enabled", e.target.checked)}
                    className="w-4 h-4 accent-red-500" />
                  <span className="text-gray-300 text-sm flex items-center gap-1.5">
                    <Image size={14} className="text-pink-400" />Pexels (Imágenes)
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={data.serpapi_enabled}
                    onChange={e => set("serpapi_enabled", e.target.checked)}
                    className="w-4 h-4 accent-red-500" />
                  <span className="text-gray-300 text-sm flex items-center gap-1.5">
                    <Search size={14} className="text-blue-400" />SerpAPI (Web)
                  </span>
                </label>
              </div>
            </>
          )}

          {tab === "models" && (
            <ComboModelSelector
              value={modelCombo}
              onChange={setModelCombo}
              freeModels={freeModels}
              paidModels={paidModels}
            />
          )}

          {tab === "voice" && (
            <VoiceTTSTab data={data} set={set} />
          )}

          {tab === "skills" && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <p className="text-gray-400 text-sm">Selecciona las habilidades que tendrá este agente</p>
                <span className="text-xs text-red-400 font-medium">
                  {(data.skills || []).length}/{skills.length} activas
                </span>
              </div>
              <div className="p-3 mb-3 bg-blue-500/8 border border-blue-500/20 rounded-xl">
                <p className="text-blue-300 text-xs">💡 Cada skill inyecta instrucciones específicas en el system prompt del agente, permitiéndole comportarse de forma especializada en esa área.</p>
              </div>
              <div className="grid grid-cols-1 gap-2 max-h-72 overflow-y-auto pr-1">
                {skills.map(skill => {
                  const isSelected = (data.skills || []).includes(skill.id);
                  const SKILL_DESCS = {
                    chat: "Conversación natural y empática", search: "Búsqueda de info actualizada",
                    images: "Mostrar imágenes en markdown", calendar: "Gestionar eventos y citas",
                    reminders: "Crear y gestionar recordatorios", notes: "Organizar notas y apuntes",
                    marketplace: "Buscar productos y precios", support: "Tickets de soporte técnico",
                    finance: "Presupuestos y análisis financiero", travel: "Planificar viajes e itinerarios",
                    music: "Recomendar música y artistas", health: "Info de salud y bienestar",
                    code: "Programación y revisión de código", translation: "Traducción multi-idioma",
                    summarize: "Resumir textos y documentos", documents: "Crear y analizar documentos",
                    analytics: "Analizar datos y crear reportes", email: "Redactar correos profesionales",
                    social: "Contenido para redes sociales", payments: "Info sobre pagos y transacciones",
                    embed_videos: "Embeber videos de YouTube, Vimeo y APIs",
                    embed_images: "Embeber imágenes y fotos de APIs vinculadas",
                    media_previews: "Lightbox para links, fotos, música y video",
                  };
                  return (
                    <div
                      key={skill.id}
                      onClick={() => toggleSkill(skill.id)}
                      className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                        isSelected ? "bg-red-500/15 border-red-500/40" : "bg-white/3 border-white/5 hover:bg-white/8"
                      }`}
                    >
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        isSelected ? "bg-red-500 text-white" : "bg-white/10 text-gray-400"
                      }`}>
                        {isSelected ? <Check size={14} /> : <Sparkles size={12} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-xs font-medium">{skill.name}</p>
                        <p className="text-gray-500 text-[10px] truncate">{SKILL_DESCS[skill.id] || skill.id}</p>
                      </div>
                      {isSelected && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-green-500/20 text-green-400 border border-green-500/30 flex-shrink-0">ACTIVA</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {tab === "knowledge" && (
            <KnowledgeAPIsPicker
              selected={data.knowledge_apis || []}
              onChange={(val) => set("knowledge_apis", val)}
            />
          )}

          {tab === "rapidapi" && (
            <RapidAPIsPicker
              selected={data.rapidapi_apis || []}
              onChange={(val) => set("rapidapi_apis", val)}
            />
          )}

          {tab === "feeds" && (
            <FeedsPicker
              selected={data.selected_feeds || []}
              onChange={(val) => set("selected_feeds", val)}
              autoSelectAll={isNew}
            />
          )}

          {tab === "integration" && (
            <div className="space-y-4">
              <div className="bg-white/5 rounded-xl p-4">
                <p className="text-gray-400 text-sm mb-1">Formato actual</p>
                <div className="flex items-center gap-2">
                  <div className="text-red-400">{FORMAT_ICONS[data.format_type]}</div>
                  <span className="text-white font-medium">
                    {formats.find(f => f.id === data.format_type)?.name || data.format_type}
                  </span>
                </div>
                <p className="text-gray-600 text-xs mt-1">
                  {formats.find(f => f.id === data.format_type)?.description}
                </p>
              </div>
              <div className="bg-white/5 rounded-xl p-4">
                <p className="text-gray-400 text-xs mb-2">El código de integración se generará cuando guardes el agente</p>
                <div className="bg-black/40 rounded-lg p-3 font-mono text-xs text-gray-400">
                  {`<!-- OkBot Agent: ${data.name || "Tu Agente"} -->`}<br />
                  {`<script src="https://tu-dominio.com/widget/okbot.js"`}<br />
                  {`  data-agent="[agent-id]"`}<br />
                  {`  data-color="${data.color || "#ef4444"}">`}<br />
                  {`</script>`}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white/5 rounded-xl p-3">
                  <p className="text-gray-400 text-xs mb-1">Categoría destino</p>
                  <div className="flex items-center gap-2">
                    <span className="text-red-400">{CATEGORY_ICONS[data.category]}</span>
                    <span className="text-white text-sm">{categories.find(c => c.id === data.category)?.name || data.category}</span>
                  </div>
                </div>
                <div className="bg-white/5 rounded-xl p-3">
                  <p className="text-gray-400 text-xs mb-1">Modelos</p>
                  <p className="text-white text-sm">{data.use_all_models ? "Combo completo" : `${(data.models || []).length} seleccionados`}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 pt-4 border-t border-white/10 flex-shrink-0">
          <button onClick={onClose} className="btn-secondary flex-1">Cancelar</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary flex-1 flex items-center justify-center gap-2">
            {saving ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
            {isNew ? "Crear Agente" : "Guardar Cambios"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Embed Code Modal ─────────────────────────────────────────────────────────
function EmbedCodeModal({ agent, onClose }) {
  const [embedData, setEmbedData] = useState(null);
  const [activeCode, setActiveCode] = useState("default");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/ai-agents/${agent.id}/embed-code`)
      .then(r => { setEmbedData(r.data); setLoading(false); })
      .catch(() => { toast.error("Error al cargar código de integración"); setLoading(false); });
  }, [agent.id]);

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Código copiado al portapapeles");
  };

  const codeOptions = embedData ? [
    { id: "default", label: "Recomendado", code: embedData.embed_code },
    { id: "widget_script", label: "Widget Script", code: embedData.all_codes?.widget_script },
    { id: "embedded_window", label: "Ventana Embebida", code: embedData.all_codes?.embedded_window },
    { id: "iframe", label: "iFrame", code: embedData.all_codes?.iframe },
  ] : [];

  return (
    <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4">
      <div className="glass-panel w-full max-w-2xl p-6 animate-scale-in max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-white font-semibold text-lg flex items-center gap-2">
              <Terminal size={18} className="text-red-400" />
              Código de Integración
            </h3>
            <p className="text-gray-500 text-xs mt-0.5">{agent.name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-2 hover:bg-white/10 rounded-lg">
            <X size={20} />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-32">
            <RefreshCw size={24} className="text-red-400 animate-spin" />
          </div>
        ) : (
          <>
            <div className="flex gap-1 mb-4 p-1 bg-white/5 rounded-lg">
              {codeOptions.map(opt => (
                <button
                  key={opt.id}
                  onClick={() => setActiveCode(opt.id)}
                  className={`flex-1 py-1.5 px-2 rounded-md text-xs font-medium transition-colors ${
                    activeCode === opt.id ? "bg-red-500/20 text-red-400" : "text-gray-400 hover:text-white"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {codeOptions.map(opt => opt.id === activeCode && (
              <div key={opt.id}>
                <div className="relative bg-black/60 rounded-xl p-4 font-mono text-xs text-gray-300 overflow-x-auto border border-white/10">
                  <pre className="whitespace-pre-wrap break-all">{opt.code}</pre>
                  <button
                    onClick={() => copyCode(opt.code)}
                    className="absolute top-3 right-3 p-2 bg-white/10 hover:bg-white/20 rounded-lg text-gray-400 hover:text-white transition-colors"
                  >
                    <Copy size={14} />
                  </button>
                </div>
              </div>
            ))}

            <div className="mt-4 grid grid-cols-3 gap-3">
              {[
                { label: "Agente ID", value: agent.id.split("-")[0] + "..." },
                { label: "Formato", value: agent.format_type?.replace("_", " ") },
                { label: "Color", value: agent.color || "#ef4444" },
              ].map(item => (
                <div key={item.label} className="bg-white/5 rounded-lg p-3">
                  <p className="text-gray-500 text-xs">{item.label}</p>
                  <p className="text-white text-sm font-mono mt-0.5">{item.value}</p>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Test Modal ───────────────────────────────────────────────────────────────
function TestModal({ agent, onClose }) {
  const [messages, setMessages] = useState([
    { role: "agent", content: agent.greeting || "¡Hola! ¿En qué puedo ayudarte?" }
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [lastModel, setLastModel] = useState(null);

  const send = async () => {
    if (!input.trim() || sending) return;
    const userMsg = input.trim();
    setInput("");
    const updatedMsgs = [...messages, { role: "user", content: userMsg }];
    setMessages(updatedMsgs);
    setSending(true);
    try {
      // Build history for the API (exclude the initial greeting, map "agent" → "assistant")
      const history = updatedMsgs.slice(1).slice(-12).map(m => ({
        role: m.role === "agent" ? "assistant" : "user",
        content: m.content
      }));
      const r = await axios.post(`${API}/admin/ai-agents/${agent.id}/chat`, {
        message: userMsg,
        history,
        user_id: "admin-test"
      });
      setMessages(m => [...m, { role: "agent", content: r.data.response }]);
      setLastModel(r.data.model_used);
    } catch {
      setMessages(m => [...m, { role: "agent", content: "Error al procesar el mensaje. Verifica que OpenRouter esté configurado." }]);
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4">
      <div className="glass-panel w-full max-w-lg animate-scale-in flex flex-col" style={{ height: "80vh" }}>
        <div className="flex items-center justify-between p-5 pb-4 border-b border-white/10 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${agent.color || "#ef4444"}25` }}>
              <Bot size={18} style={{ color: agent.color || "#ef4444" }} />
            </div>
            <div>
              <p className="text-white font-medium text-sm">{agent.name}</p>
              <div className="flex items-center gap-2">
                <p className="text-gray-500 text-xs">{lastModel ? `Usando: ${lastModel.split("/").pop()}` : "Modo prueba"}</p>
                {(agent.skills || []).length > 0 && (
                  <span className="text-xs px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-400 border border-red-500/30">
                    {agent.skills.length} skills
                  </span>
                )}
                {(agent.selected_feeds || []).length > 0 && (
                  <span className="text-xs px-1.5 py-0.5 rounded-full bg-blue-500/20 text-blue-400 border border-blue-500/30">
                    {agent.selected_feeds.length} feeds
                  </span>
                )}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-2 hover:bg-white/10 rounded-lg">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-xs px-4 py-2.5 rounded-2xl text-sm ${
                msg.role === "user"
                  ? "bg-red-500 text-white rounded-br-sm"
                  : "bg-white/10 text-gray-200 rounded-bl-sm"
              }`}>
                {msg.content}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-white/10 px-4 py-2.5 rounded-2xl rounded-bl-sm">
                <div className="flex gap-1">
                  {[0, 1, 2].map(i => (
                    <div key={i} className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-white/10 flex-shrink-0">
          <div className="flex gap-2">
            <input
              type="text" value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && send()}
              placeholder="Escribe un mensaje..."
              className="input-glass flex-1 text-sm"
              disabled={sending}
            />
            <button onClick={send} disabled={sending || !input.trim()} className="btn-primary px-4">
              {sending ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Agent Card ───────────────────────────────────────────────────────────────
function AgentCard({ agent, categories, formats, onEdit, onTest, onToggle, onDelete, onEmbed }) {
  const cat = categories.find(c => c.id === agent.category);
  const fmt = formats.find(f => f.id === agent.format_type);

  return (
    <div className={`glass-panel p-5 flex flex-col transition-all ${agent.is_active ? "" : "opacity-60"}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: `${agent.color || "#ef4444"}20` }}>
            <Bot size={22} style={{ color: agent.color || "#ef4444" }} />
          </div>
          <div>
            <h3 className="text-white font-semibold text-sm">{agent.name}</h3>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="text-gray-500 text-xs flex items-center gap-1">
                {CATEGORY_ICONS[agent.category]}{cat?.name || agent.category}
              </span>
            </div>
          </div>
        </div>
        <button onClick={() => onToggle(agent.id)} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors flex-shrink-0">
          {agent.is_active
            ? <ToggleRight size={22} className="text-green-400" />
            : <ToggleLeft size={22} className="text-gray-500" />}
        </button>
      </div>

      {agent.description && (
        <p className="text-gray-400 text-xs mb-3 line-clamp-2">{agent.description}</p>
      )}

      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="bg-white/5 rounded-lg p-2 text-center">
          <p className="text-white text-sm font-bold">{agent.stats?.total_conversations || 0}</p>
          <p className="text-gray-600 text-xs">Chats</p>
        </div>
        <div className="bg-white/5 rounded-lg p-2 text-center">
          <p className="text-white text-sm font-bold">{(agent.skills || []).length}</p>
          <p className="text-gray-600 text-xs">Skills</p>
        </div>
        <div className="bg-white/5 rounded-lg p-2 text-center">
          <p className="text-white text-sm font-bold">{agent.use_all_models ? "∞" : (agent.models || []).length}</p>
          <p className="text-gray-600 text-xs">Modelos</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 mb-3">
        {(agent.skills || []).slice(0, 4).map(s => (
          <span key={s} className="text-xs px-2 py-0.5 bg-white/5 text-gray-400 rounded-full">{s}</span>
        ))}
        {(agent.skills || []).length > 4 && (
          <span className="text-xs px-2 py-0.5 bg-white/5 text-gray-500 rounded-full">+{agent.skills.length - 4}</span>
        )}
      </div>

      <div className="flex items-center gap-2 mb-4 text-xs text-gray-600">
        <span className="flex items-center gap-1">
          {FORMAT_ICONS[agent.format_type]}{fmt?.name || agent.format_type}
        </span>
        {agent.pexels_enabled && <span className="flex items-center gap-1"><Image size={10} />Pexels</span>}
        {agent.serpapi_enabled && <span className="flex items-center gap-1"><Search size={10} />Web</span>}
      </div>

      <div className="flex gap-1.5 mt-auto">
        <button onClick={() => onEdit(agent)}
          className="btn-secondary text-xs flex-1 flex items-center justify-center gap-1 py-2">
          <Edit size={12} />Editar
        </button>
        <button onClick={() => onTest(agent)}
          className="btn-secondary text-xs flex-1 flex items-center justify-center gap-1 py-2">
          <Play size={12} />Probar
        </button>
        <button onClick={() => onEmbed(agent)}
          className="btn-secondary text-xs px-2.5 py-2 flex items-center justify-center">
          <Code size={12} />
        </button>
        <button onClick={() => onDelete(agent.id)}
          className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors">
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

// ─── Main Agent Modal ─────────────────────────────────────────────────────────
function MainAgentModal({ freeModels, paidModels, skills, onClose }) {
  const DEFAULT_CONFIG = {
    name: "OkBot Principal",
    system_prompt: "Eres OkBot, un asistente personal inteligente y amigable. Ayudas a los usuarios con todas sus necesidades: recordatorios, finanzas, viajes, salud, música, compras y más.",
    greeting: "¡Hola! Soy OkBot, tu asistente personal. ¿En qué puedo ayudarte hoy?",
    model_combo: "free",
    skills: [],
    knowledge_apis: [],
    rapidapi_apis: [],
    selected_feeds: [],
    pexels_enabled: true,
    serpapi_enabled: true,
    super_fast_response: false,
    color: "#ef4444",
    is_main_orchestrator: true,
    max_context_messages: 20,
    nim_preferred_tier: "tier1",
    wa_cascade_mode: "nim_fast",
    enable_mcp_tools: true,
    orchestrator_delegation: true,
    tokenra_enabled: false,
    tokenra_model: "",
    jev_enabled: true,
    jev_model_routing: true,
    jev_task_routing: true,
    jev_tool_guard: true,
    jev_completion_review: true,
    jev_research_review: false,
    // Voz IA / TTS
    tts_enabled: false,
    tts_provider: "browser",
    tts_voice_id: "",
    tts_speed: 1.0,
    tts_fish_emotion: "neutral",
    tts_fallback: "browser",
    tts_auto_narrate: false,
  };

  const [tab, setTab] = useState("basic");
  const [data, setData] = useState(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const set = (field, val) => setData(d => ({ ...d, [field]: val }));
  const toggleSkill = (id) => {
    const list = data.skills || [];
    set("skills", list.includes(id) ? list.filter(s => s !== id) : [...list, id]);
  };

  useEffect(() => {
    axios.get(`${API}/admin/ai-agents/main-agent`)
      .then(r => { setData({ ...DEFAULT_CONFIG, ...r.data }); setLoading(false); })
      .catch(() => { setData(DEFAULT_CONFIG); setLoading(false); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    if (!data.name || !data.system_prompt) {
      toast.error("Nombre y System Prompt son requeridos");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/ai-agents/main-agent`, data);
      toast.success("Agente principal actualizado correctamente");
      onClose();
    } catch {
      toast.error("Error al guardar configuración");
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: "basic",       label: "Configuración",    icon: <Settings size={14} /> },
    { id: "models",      label: "Modelos",          icon: <Zap size={14} /> },
    { id: "voice",       label: "Voz IA",           icon: <Mic size={14} className="text-pink-400" /> },
    { id: "skills",      label: "Skills",           icon: <Sparkles size={14} /> },
    { id: "knowledge",   label: "APIs Conocimiento",icon: <Database size={14} /> },
    { id: "rapidapi",    label: "RapidAPI Hub",     icon: <Zap size={14} className="text-yellow-400" /> },
    { id: "feeds",       label: "Feeds",            icon: <Rss size={14} /> },
    { id: "integration", label: "Integración",      icon: <Code size={14} /> },
    { id: "nvidia",      label: "NVIDIA NIM",       icon: <Cpu size={14} className="text-green-400" /> },
    { id: "advanced",    label: "Avanzado",         icon: <Sliders size={14} className="text-purple-400" /> },
  ];

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4">
        <div className="glass-panel w-full max-w-2xl p-12 flex items-center justify-center">
          <RefreshCw size={24} className="text-red-400 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4">
      <div className="glass-panel w-full max-w-2xl animate-scale-in flex flex-col" style={{ maxHeight: "92vh" }}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-4 border-b border-white/10 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center">
              <Bot size={18} className="text-white" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-lg">Agente Principal del Chat</h3>
              <p className="text-gray-500 text-xs mt-0.5">Configura el comportamiento del chat del dashboard</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-2 hover:bg-white/10 rounded-lg">
            <X size={20} />
          </button>
        </div>

        {/* Tabs — scrollable for 9 tabs */}
        <div className="flex gap-1 p-4 pb-0 flex-shrink-0 overflow-x-auto scrollbar-none">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                tab === t.id ? "bg-red-500/20 text-red-400 border border-red-500/30" : "text-gray-400 hover:bg-white/5"
              }`}
            >
              {t.icon}{t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {tab === "basic" && (
            <>
              <div>
                <label className="block text-gray-400 text-xs mb-1.5 font-medium">Nombre del Agente *</label>
                <input
                  type="text" value={data.name}
                  onChange={e => set("name", e.target.value)}
                  placeholder="OkBot Principal"
                  className="input-glass w-full"
                />
              </div>

              <div>
                <label className="block text-gray-400 text-xs mb-1.5 font-medium">Saludo Inicial</label>
                <input
                  type="text" value={data.greeting}
                  onChange={e => set("greeting", e.target.value)}
                  placeholder="¡Hola! ¿En qué puedo ayudarte?"
                  className="input-glass w-full"
                />
              </div>

              <div>
                <label className="block text-gray-400 text-xs mb-1.5 font-medium">System Prompt *</label>
                <textarea
                  value={data.system_prompt}
                  onChange={e => set("system_prompt", e.target.value)}
                  placeholder="Eres OkBot, un asistente personal inteligente..."
                  className="input-glass w-full resize-none"
                  rows={6}
                />
                <p className="text-gray-600 text-xs mt-1">
                  Define la personalidad, función y comportamiento del agente principal del chat
                </p>
              </div>

              <div>
                <label className="block text-gray-400 text-xs mb-2 font-medium">Color del Agente</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color" value={data.color || "#ef4444"}
                    onChange={e => set("color", e.target.value)}
                    className="w-10 h-10 rounded-lg cursor-pointer bg-transparent border-0"
                  />
                  <div className="flex gap-2">
                    {["#ef4444", "#8b5cf6", "#3b82f6", "#10b981", "#f59e0b", "#ec4899"].map(c => (
                      <button
                        key={c} onClick={() => set("color", c)}
                        className="w-6 h-6 rounded-full border-2 transition-transform hover:scale-110"
                        style={{ background: c, borderColor: data.color === c ? "white" : "transparent" }}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={data.pexels_enabled}
                    onChange={e => set("pexels_enabled", e.target.checked)}
                    className="w-4 h-4 accent-red-500" />
                  <span className="text-gray-300 text-sm flex items-center gap-1.5">
                    <Image size={14} className="text-pink-400" />Pexels (Imágenes)
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={data.serpapi_enabled}
                    onChange={e => set("serpapi_enabled", e.target.checked)}
                    className="w-4 h-4 accent-red-500" />
                  <span className="text-gray-300 text-sm flex items-center gap-1.5">
                    <Search size={14} className="text-blue-400" />SerpAPI (Web)
                  </span>
                </label>
              </div>

              {/* ⚡ Super Fast Response */}
              <div
                className={`p-4 rounded-2xl border cursor-pointer transition-all ${data.super_fast_response ? 'bg-yellow-500/10 border-yellow-500/40' : 'bg-white/3 border-white/10 hover:bg-white/5'}`}
                onClick={() => set("super_fast_response", !data.super_fast_response)}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${data.super_fast_response ? 'bg-yellow-500 text-black' : 'bg-white/10 text-gray-400'}`}>
                      <Zap size={18} />
                    </div>
                    <div>
                      <p className={`text-sm font-semibold ${data.super_fast_response ? 'text-yellow-400' : 'text-white'}`}>⚡ Respuesta Super Rápida</p>
                      <p className="text-gray-500 text-xs mt-0.5">Fuerza al agente a responder en máximo 3 oraciones. Incluye botón "Dame más detalles" al final.</p>
                    </div>
                  </div>
                  <div className={`w-11 h-6 rounded-full transition-colors flex-shrink-0 relative ${data.super_fast_response ? 'bg-yellow-500' : 'bg-white/15'}`}>
                    <div className="absolute top-0.5 transition-all bg-white rounded-full" style={{ width: 20, height: 20, left: data.super_fast_response ? 'calc(100% - 22px)' : 2 }} />
                  </div>
                </div>
                {data.super_fast_response && (
                  <p className="text-yellow-400 text-xs mt-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg px-3 py-2">
                    ✓ Activo — Respuestas brevísimas (~3 oraciones). El chat mostrará un botón para pedir respuesta detallada.
                  </p>
                )}
              </div>
            </>
          )}

          {tab === "models" && (
            <ComboModelSelector
              value={data.model_combo || "free"}
              onChange={(val) => set("model_combo", val)}
              freeModels={freeModels}
              paidModels={paidModels}
            />
          )}

          {tab === "voice" && (
            <VoiceTTSTab data={data} set={set} />
          )}

          {tab === "skills" && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <p className="text-gray-400 text-sm">Habilidades del agente principal</p>
                <span className="text-xs text-red-400 font-medium">{(data.skills || []).length}/{skills.length} activas</span>
              </div>
              <div className="p-3 mb-3 bg-blue-500/8 border border-blue-500/20 rounded-xl">
                <p className="text-blue-300 text-xs">💡 Cada skill inyecta instrucciones especializadas en el system prompt, definiendo cómo responde el agente al usuario.</p>
              </div>
              <div className="grid grid-cols-1 gap-2 max-h-72 overflow-y-auto pr-1">
                {skills.map(skill => {
                  const isSelected = (data.skills || []).includes(skill.id);
                  const SKILL_DESCS = {
                    chat: "Conversación natural y empática", search: "Búsqueda de info actualizada",
                    images: "Mostrar imágenes en markdown", calendar: "Gestionar eventos y citas",
                    reminders: "Crear y gestionar recordatorios", notes: "Organizar notas y apuntes",
                    marketplace: "Buscar productos y precios", support: "Tickets de soporte técnico",
                    finance: "Presupuestos y análisis financiero", travel: "Planificar viajes e itinerarios",
                    music: "Recomendar música y artistas", health: "Info de salud y bienestar",
                    code: "Programación y revisión de código", translation: "Traducción multi-idioma",
                    summarize: "Resumir textos y documentos", documents: "Crear y analizar documentos",
                    analytics: "Analizar datos y crear reportes", email: "Redactar correos profesionales",
                    social: "Contenido para redes sociales", payments: "Info sobre pagos y transacciones",
                    embed_videos: "Embeber videos de YouTube, Vimeo y APIs",
                    embed_images: "Embeber imágenes y fotos de APIs vinculadas",
                    media_previews: "Lightbox para links, fotos, música y video",
                  };
                  return (
                    <div key={skill.id} onClick={() => toggleSkill(skill.id)}
                      className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                        isSelected ? "bg-red-500/15 border-red-500/40" : "bg-white/3 border-white/5 hover:bg-white/8"
                      }`}
                    >
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${isSelected ? "bg-red-500 text-white" : "bg-white/10 text-gray-400"}`}>
                        {isSelected ? <Check size={14} /> : <Sparkles size={12} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-xs font-medium">{skill.name}</p>
                        <p className="text-gray-500 text-[10px] truncate">{SKILL_DESCS[skill.id] || skill.id}</p>
                      </div>
                      {isSelected && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-green-500/20 text-green-400 border border-green-500/30 flex-shrink-0">ACTIVA</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {tab === "knowledge" && (
            <KnowledgeAPIsPicker
              selected={data.knowledge_apis || []}
              onChange={(val) => set("knowledge_apis", val)}
            />
          )}

          {tab === "rapidapi" && (
            <RapidAPIsPicker
              selected={data.rapidapi_apis || []}
              onChange={(val) => set("rapidapi_apis", val)}
            />
          )}

          {tab === "feeds" && (
            <FeedsPicker
              selected={data.selected_feeds || []}
              onChange={(val) => set("selected_feeds", val)}
              autoSelectAll={true}
            />
          )}

          {/* ── Integración ─────────────────────────────────────────────── */}
          {tab === "integration" && (
            <div className="space-y-4">
              <div className="bg-gradient-to-br from-red-500/10 to-purple-500/10 border border-red-500/20 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center">
                    <Network size={18} className="text-red-400" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">OkBot Jarvis — Orquestador Principal</p>
                    <p className="text-gray-500 text-xs">Agente central del sistema · Delegación multi-agente</p>
                  </div>
                  <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 border border-green-500/30 flex-shrink-0">● Activo</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-black/20 rounded-lg p-3">
                    <p className="text-gray-500 text-xs mb-1">Endpoint chat principal</p>
                    <code className="text-green-400 text-xs font-mono">POST /api/chat/send</code>
                  </div>
                  <div className="bg-black/20 rounded-lg p-3">
                    <p className="text-gray-500 text-xs mb-1">Configuración</p>
                    <code className="text-blue-400 text-xs font-mono">GET /api/admin/ai-agents/main-agent</code>
                  </div>
                  <div className="bg-black/20 rounded-lg p-3">
                    <p className="text-gray-500 text-xs mb-1">Model combo activo</p>
                    <span className={`text-xs font-bold ${data.model_combo === "nvidia" ? "text-green-400" : data.model_combo === "super" ? "text-purple-400" : "text-blue-400"}`}>
                      {data.model_combo || "free"}
                    </span>
                  </div>
                  <div className="bg-black/20 rounded-lg p-3">
                    <p className="text-gray-500 text-xs mb-1">Skills activas</p>
                    <span className="text-white text-xs font-bold">{(data.skills || []).length}</span>
                    <span className="text-gray-500 text-xs"> configuradas</span>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider">Capacidades de Orquestación</p>
                {[
                  { label: "Delegación a agentes expertos",    desc: "Mercader, Doctor, Financiero, Cinéfilo, etc.", active: data.orchestrator_delegation },
                  { label: "Herramientas MCP (45 tools)",       desc: "Calendar, Notes, Search, Finance, Health…",   active: data.enable_mcp_tools },
                  { label: "Memoria persistente del usuario",   desc: "Contexto inyectado en cada conversación",      active: true },
                  { label: "Cascade multi-modelo",              desc: "Fallback automático entre proveedores",         active: true },
                  { label: "Canal WhatsApp directo",            desc: "Meta Cloud API via webhook okbot.okgo.mx",     active: true },
                ].map((cap, i) => (
                  <div key={i} className={`flex items-center gap-3 p-3 rounded-xl border ${cap.active ? "bg-green-500/8 border-green-500/20" : "bg-white/3 border-white/5"}`}>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${cap.active ? "bg-green-500/30" : "bg-gray-500/20"}`}>
                      {cap.active ? <Check size={12} className="text-green-400" /> : <X size={12} className="text-gray-500" />}
                    </div>
                    <div>
                      <p className={`text-xs font-medium ${cap.active ? "text-white" : "text-gray-500"}`}>{cap.label}</p>
                      <p className="text-gray-600 text-[10px]">{cap.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <p className="text-gray-400 text-xs mb-2 font-medium">Embed en módulo externo</p>
                <div className="bg-black/40 rounded-lg p-3 font-mono text-xs text-gray-400">
                  <span className="text-gray-600">{`// OkBot Jarvis — Agente Principal`}</span><br />
                  <span className="text-blue-400">POST</span> <span className="text-green-400">/api/chat/send</span><br />
                  <span className="text-gray-600">{"{ user_id, message, context }"}</span>
                </div>
              </div>
            </div>
          )}

          {/* ── NVIDIA NIM ──────────────────────────────────────────────── */}
          {tab === "nvidia" && (
            <div className="space-y-5">
              {/* Status + combo toggle */}
              <div className={`p-4 rounded-xl border ${data.model_combo === "nvidia" ? "bg-green-500/10 border-green-500/30" : "bg-white/5 border-white/10"}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-green-500/20 flex items-center justify-center">
                      <Cpu size={20} className="text-green-400" />
                    </div>
                    <div>
                      <p className="text-white font-semibold">NVIDIA NIM</p>
                      <p className="text-gray-500 text-xs">Inference Microservices · 28 modelos · Sin proxy</p>
                    </div>
                  </div>
                  <button
                    onClick={() => set("model_combo", data.model_combo === "nvidia" ? "super" : "nvidia")}
                    className={`px-4 py-2 rounded-lg text-xs font-semibold transition-colors border ${
                      data.model_combo === "nvidia"
                        ? "bg-green-500/20 text-green-300 border-green-500/40"
                        : "bg-white/10 text-gray-300 border-white/20 hover:bg-white/15"
                    }`}
                  >
                    {data.model_combo === "nvidia" ? "✓ Combo activo" : "Activar como combo"}
                  </button>
                </div>
                {data.model_combo === "nvidia" && (
                  <div className="mt-3 p-2.5 rounded-lg bg-green-500/10 border border-green-500/20">
                    <p className="text-green-300 text-xs">✅ Jarvis usa NIM cascade: DeepSeek V4 Flash → GPT OSS 20B → Llama 3.2 3B → fallback tier 2</p>
                  </div>
                )}
              </div>

              {/* Tier indicator */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider">Prioridad de Cascade</p>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: "tier1", label: "Tier 1 — Ultra rápido", desc: "<0.5s · sin cold-start", color: "green" },
                    { id: "tier2", label: "Tier 2 — Balanceado",   desc: "1-3s · cold-start posible", color: "yellow" },
                    { id: "tier3", label: "Tier 3 — Modelos XXL",  desc: "30-90s❄ · cold-start", color: "red" },
                  ].map(tier => (
                    <div
                      key={tier.id}
                      onClick={() => set("nim_preferred_tier", tier.id)}
                      className={`p-3 rounded-xl border cursor-pointer transition-colors ${
                        data.nim_preferred_tier === tier.id
                          ? `bg-${tier.color}-500/15 border-${tier.color}-500/40`
                          : "bg-white/3 border-white/5 hover:bg-white/8"
                      }`}
                    >
                      <p className={`text-xs font-medium ${data.nim_preferred_tier === tier.id ? "text-white" : "text-gray-400"}`}>{tier.label}</p>
                      <p className="text-gray-600 text-[10px] mt-0.5">{tier.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tier 1 */}
              <div>
                <p className="text-green-400 text-xs font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-green-400 inline-block" />
                  Tier 1 — Confirmados rápidos (benchmarked 2026-07)
                </p>
                <div className="space-y-1.5">
                  {NIM_TIERS.tier1.map(m => (
                    <div key={m.id} className="flex items-center justify-between bg-green-500/8 border border-green-500/15 rounded-lg px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span className="text-white text-xs font-medium">{m.label}</span>
                        <span className="text-gray-500 text-[10px]">{m.params}</span>
                        {m.tool && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-500/20 text-blue-400 border border-blue-500/25">tools</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500 text-[10px]">{m.provider}</span>
                        <span className="text-green-400 text-xs font-mono">{m.latency}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tier 2 */}
              <div>
                <p className="text-yellow-400 text-xs font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-yellow-400 inline-block" />
                  Tier 2 — Balance calidad/velocidad
                </p>
                <div className="space-y-1.5">
                  {NIM_TIERS.tier2.map(m => (
                    <div key={m.id} className="flex items-center justify-between bg-white/3 border border-white/8 rounded-lg px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span className="text-white text-xs font-medium">{m.label}</span>
                        <span className="text-gray-600 text-[10px]">{m.params}</span>
                        {m.tool && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-500/20 text-blue-400 border border-blue-500/25">tools</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-600 text-[10px]">{m.provider}</span>
                        <span className="text-yellow-400 text-xs font-mono">{m.latency}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tier 3 */}
              <div>
                <p className="text-red-400 text-xs font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-red-400 inline-block" />
                  Tier 3 — Modelos masivos (cold-start probable)
                </p>
                <div className="space-y-1.5">
                  {NIM_TIERS.tier3.map(m => (
                    <div key={m.id} className="flex items-center justify-between bg-white/3 border border-white/8 rounded-lg px-3 py-2 opacity-75">
                      <div className="flex items-center gap-2">
                        <span className="text-white text-xs font-medium">{m.label}</span>
                        <span className="text-gray-600 text-[10px]">{m.params}</span>
                        {m.tool && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-500/20 text-blue-400 border border-blue-500/25">tools</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-600 text-[10px]">{m.provider}</span>
                        <span className="text-red-400 text-xs font-mono">{m.latency}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-blue-500/8 border border-blue-500/20 rounded-xl p-4">
                <p className="text-blue-300 text-xs font-medium mb-1">💡 ¿Cómo funciona el cascade NIM?</p>
                <p className="text-gray-400 text-xs">Jarvis intenta el modelo más rápido de Tier 1 primero. Si hay error (rate limit, cold-start, timeout), automáticamente baja al siguiente. La variable <code className="text-green-400">model_combo: "nvidia"</code> activa este comportamiento.</p>
              </div>
            </div>
          )}

          {/* ── Funciones Avanzadas ─────────────────────────────────────── */}
          {tab === "advanced" && (
            <div className="space-y-5">
              {/* Orchestrator mode */}
              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <p className="text-white text-sm font-semibold mb-3 flex items-center gap-2">
                  <GitBranch size={15} className="text-purple-400" />Modo Orquestador
                </p>
                <div className="space-y-3">
                  <div
                    onClick={() => set("orchestrator_delegation", !data.orchestrator_delegation)}
                    className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-colors ${data.orchestrator_delegation ? "bg-purple-500/10 border-purple-500/30" : "bg-white/3 border-white/5 hover:bg-white/8"}`}
                  >
                    <div>
                      <p className="text-white text-xs font-medium">Delegación inteligente a sub-agentes</p>
                      <p className="text-gray-500 text-[10px]">Detecta contexto y deriva a Mercader, Doctor, Financiero, etc.</p>
                    </div>
                    <div className={`w-9 h-5 rounded-full transition-colors relative flex-shrink-0 ${data.orchestrator_delegation ? "bg-purple-500" : "bg-white/15"}`}>
                      <div className="absolute top-0.5 transition-all bg-white rounded-full" style={{ width: 16, height: 16, left: data.orchestrator_delegation ? "calc(100% - 18px)" : 2 }} />
                    </div>
                  </div>
                  <div
                    onClick={() => set("enable_mcp_tools", !data.enable_mcp_tools)}
                    className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-colors ${data.enable_mcp_tools ? "bg-blue-500/10 border-blue-500/30" : "bg-white/3 border-white/5 hover:bg-white/8"}`}
                  >
                    <div>
                      <p className="text-white text-xs font-medium">Herramientas MCP (45 tools)</p>
                      <p className="text-gray-500 text-[10px]">Calendar, Notes, Memory, Search, Finance, Health, Travel…</p>
                    </div>
                    <div className={`w-9 h-5 rounded-full transition-colors relative flex-shrink-0 ${data.enable_mcp_tools ? "bg-blue-500" : "bg-white/15"}`}>
                      <div className="absolute top-0.5 transition-all bg-white rounded-full" style={{ width: 16, height: 16, left: data.enable_mcp_tools ? "calc(100% - 18px)" : 2 }} />
                    </div>
                  </div>
                  <div
                    onClick={() => set("is_main_orchestrator", !data.is_main_orchestrator)}
                    className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-colors ${data.is_main_orchestrator ? "bg-red-500/10 border-red-500/30" : "bg-white/3 border-white/5 hover:bg-white/8"}`}
                  >
                    <div>
                      <p className="text-white text-xs font-medium">Modo agente principal del sistema</p>
                      <p className="text-gray-500 text-[10px]">Usa el system prompt extendido del orquestador Jarvis</p>
                    </div>
                    <div className={`w-9 h-5 rounded-full transition-colors relative flex-shrink-0 ${data.is_main_orchestrator ? "bg-red-500" : "bg-white/15"}`}>
                      <div className="absolute top-0.5 transition-all bg-white rounded-full" style={{ width: 16, height: 16, left: data.is_main_orchestrator ? "calc(100% - 18px)" : 2 }} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Context & Memory */}
              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <p className="text-white text-sm font-semibold mb-3 flex items-center gap-2">
                  <Database size={15} className="text-blue-400" />Contexto y Memoria
                </p>
                <div>
                  <label className="block text-gray-400 text-xs mb-2 font-medium">
                    Mensajes en contexto — <span className="text-white">{data.max_context_messages || 20}</span>
                  </label>
                  <input
                    type="range" min={5} max={50} step={5}
                    value={data.max_context_messages || 20}
                    onChange={e => set("max_context_messages", Number(e.target.value))}
                    className="w-full accent-blue-500"
                  />
                  <div className="flex justify-between text-gray-600 text-[10px] mt-1">
                    <span>5 (ligero)</span><span>25 (balance)</span><span>50 (máximo)</span>
                  </div>
                  <p className="text-gray-500 text-xs mt-2">Cuántos mensajes del historial se envían al modelo. Más = más contexto, más tokens/costo.</p>
                </div>
              </div>

              {/* WhatsApp behavior */}
              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <p className="text-white text-sm font-semibold mb-3 flex items-center gap-2">
                  <MessageCircle size={15} className="text-green-400" />Comportamiento WhatsApp
                </p>
                <div>
                  <label className="block text-gray-400 text-xs mb-2 font-medium">Cascade WA</label>
                  <select
                    value={data.wa_cascade_mode || "nim_fast"}
                    onChange={e => set("wa_cascade_mode", e.target.value)}
                    className="input-glass w-full text-sm"
                  >
                    <option value="nim_fast">NIM Tier 1 rápido (recomendado)</option>
                    <option value="nim_full">NIM cascade completo</option>
                    <option value="openrouter">Solo OpenRouter</option>
                    <option value="auto">Auto (detecta mejor modelo)</option>
                  </select>
                  <p className="text-gray-500 text-xs mt-2">Define qué modelo responde los mensajes entrantes de WhatsApp. NIM Tier 1 garantiza respuesta en {"<"}1s.</p>
                </div>
              </div>

              {/* Performance */}
              <div className="bg-yellow-500/8 border border-yellow-500/20 rounded-xl p-4">
                <p className="text-yellow-400 text-xs font-medium mb-1">⚡ Respuesta Super Rápida</p>
                <p className="text-gray-400 text-xs mb-3">Esta opción también está en la pestaña Configuración.</p>
                <div
                  onClick={() => set("super_fast_response", !data.super_fast_response)}
                  className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-colors ${data.super_fast_response ? "bg-yellow-500/15 border-yellow-500/40" : "bg-white/3 border-white/5"}`}
                >
                  <div>
                    <p className="text-white text-xs font-medium">Forzar respuesta en máx. 3 oraciones</p>
                    <p className="text-gray-500 text-[10px]">Incluye botón "Dame más detalles" al final de cada respuesta</p>
                  </div>
                  <div className={`w-9 h-5 rounded-full transition-colors relative flex-shrink-0 ${data.super_fast_response ? "bg-yellow-500" : "bg-white/15"}`}>
                    <div className="absolute top-0.5 transition-all bg-white rounded-full" style={{ width: 16, height: 16, left: data.super_fast_response ? "calc(100% - 18px)" : 2 }} />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 pt-4 border-t border-white/10 flex-shrink-0">
          <button onClick={onClose} className="btn-secondary flex-1">Cancelar</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary flex-1 flex items-center justify-center gap-2">
            {saving ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
            Guardar Agente Principal
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
const DEFAULT_AGENT = {
  name: "", description: "", greeting: "¡Hola! ¿En qué puedo ayudarte?",
  system_prompt: "", skills: [], category: "usuario", format_type: "floating_button",
  models: [], use_all_models: false, model_combo: "super", pexels_enabled: true, serpapi_enabled: true,
  knowledge_apis: [], rapidapi_apis: [], selected_feeds: [], is_active: true, color: "#ef4444", icon: "bot"
};

export default function AIAgentsManager() {
  const [agents, setAgents] = useState([]);
  const [openRouterStatus, setOpenRouterStatus] = useState(null);
  const [tokenRaStatus, setTokenRaStatus] = useState(null);
  const [jevStatus, setJevStatus] = useState(null);
  const [freeModels, setFreeModels] = useState([]);
  const [paidModels, setPaidModels] = useState([]);
  const [categories, setCategories] = useState([]);
  const [formats, setFormats] = useState([]);
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [testingAgent, setTestingAgent] = useState(null);
  const [embedAgent, setEmbedAgent] = useState(null);
  const [showMainAgent, setShowMainAgent] = useState(false);
  const [mainAgentSummary, setMainAgentSummary] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [agentsR, statusR, tokenRaR, jevR, modelsR, catsR, fmtsR, sklsR, mainR] = await Promise.all([
        axios.get(`${API}/admin/ai-agents/`),
        axios.get(`${API}/admin/ai-agents/openrouter/status`),
        axios.get(`${API}/admin/ai-agents/tokenra/status`).catch(() => ({ data: null })),
        axios.get(`${API}/admin/ai-agents/jev/status`).catch(() => ({ data: null })),
        axios.get(`${API}/admin/ai-agents/openrouter/models`),
        axios.get(`${API}/admin/ai-agents/categories/list`),
        axios.get(`${API}/admin/ai-agents/formats/list`),
        axios.get(`${API}/admin/ai-agents/skills/list`),
        axios.get(`${API}/admin/ai-agents/main-agent`).catch(() => ({ data: null })),
      ]);
      setAgents(agentsR.data.filter(a => a.id !== "main"));
      setOpenRouterStatus(statusR.data);
      setTokenRaStatus(tokenRaR.data);
      setJevStatus(jevR.data);
      setFreeModels(modelsR.data.models || []);
      setPaidModels(modelsR.data.paid_models || []);
      setCategories(catsR.data.categories || []);
      setFormats(fmtsR.data.formats || []);
      setSkills(sklsR.data.skills || []);
      setMainAgentSummary(mainR.data);
    } catch (e) {
      console.error("Error loading AI agents:", e);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCreate = async (data) => {
    try {
      await axios.post(`${API}/admin/ai-agents/`, data);
      toast.success("Agente creado exitosamente");
      setShowCreate(false);
      loadData();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Error al crear agente";
      toast.error(msg);
      throw e;
    }
  };

  const handleUpdate = async (data) => {
    try {
      await axios.put(`${API}/admin/ai-agents/${data.id}`, data);
      toast.success("Agente actualizado correctamente");
      setEditingAgent(null);
      loadData();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Error al actualizar agente";
      toast.error(msg);
      throw e;
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este agente?")) return;
    await axios.delete(`${API}/admin/ai-agents/${id}`);
    toast.success("Agente eliminado");
    loadData();
  };

  const handleToggle = async (id) => {
    const r = await axios.put(`${API}/admin/ai-agents/${id}/toggle`);
    toast.success(r.data.is_active ? "Agente activado" : "Agente desactivado");
    loadData();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-red-400 animate-spin" />
      </div>
    );
  }

  const activeCount = agents.filter(a => a.is_active).length;

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Bot size={22} className="text-red-400" />Agentes IA
          </h2>
          <p className="text-gray-500 text-sm mt-0.5">Orquesta agentes con Jev, TokenRa, NVIDIA y OpenRouter</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={loadData} className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
            <RefreshCw size={16} />
          </button>
          <button onClick={() => setShowCreate(true)} className="btn-primary flex items-center gap-2">
            <Plus size={16} />Nuevo Agente
          </button>
        </div>
      </div>

      {/* ── OkBot Jarvis Hero Panel ──────────────────────────────────────── */}
      <div className="glass-panel p-5 mb-6 bg-gradient-to-r from-red-500/8 to-purple-500/8 border border-red-500/25">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-4 flex-1">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-red-500 to-purple-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-red-500/20">
              <Bot size={26} className="text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <h3 className="text-white font-bold text-base">🤖 OkBot Jarvis</h3>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 border border-green-500/30 font-medium">● Activo</span>
              </div>
              <p className="text-gray-400 text-sm">Orquestador Principal del Sistema</p>
              <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                <span className="text-gray-600 text-xs flex items-center gap-1">
                  <Cpu size={11} className="text-green-400" />
                  <span className="text-gray-400">combo:</span>
                  <span className={`font-semibold ${mainAgentSummary?.model_combo === "nvidia" ? "text-green-400" : mainAgentSummary?.model_combo === "super" ? "text-purple-400" : "text-blue-400"}`}>
                    {mainAgentSummary?.model_combo || "nvidia"}
                  </span>
                </span>
                <span className="text-gray-600 text-xs flex items-center gap-1">
                  <Sparkles size={11} className="text-yellow-400" />
                  <span className="text-gray-400">{(mainAgentSummary?.skills || []).length} skills</span>
                </span>
                <span className="text-gray-600 text-xs flex items-center gap-1">
                  <Database size={11} className="text-blue-400" />
                  <span className="text-gray-400">{(mainAgentSummary?.knowledge_apis || []).length} APIs</span>
                </span>
                <span className="text-gray-600 text-xs flex items-center gap-1">
                  <Rss size={11} className="text-orange-400" />
                  <span className="text-gray-400">{(mainAgentSummary?.selected_feeds || []).length} feeds</span>
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={() => setShowMainAgent(true)}
            className="btn-secondary flex items-center gap-2 text-sm border border-red-500/30 text-red-400 hover:bg-red-500/10 flex-shrink-0"
          >
            <Settings size={15} />Configurar Jarvis
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-4 pt-4 border-t border-white/8">
          {[
            { label: "Delegación multi-agente",    active: mainAgentSummary?.orchestrator_delegation !== false, icon: <GitBranch size={11} /> },
            { label: "Herramientas MCP (45)",       active: mainAgentSummary?.enable_mcp_tools !== false,       icon: <Network size={11} /> },
            { label: "Respuesta super rápida",      active: !!mainAgentSummary?.super_fast_response,            icon: <Zap size={11} /> },
            { label: "NVIDIA NIM cascade",          active: mainAgentSummary?.model_combo === "nvidia",         icon: <Cpu size={11} /> },
            { label: "Jev Decision Layer",           active: jevStatus?.ready,                                  icon: <Shield size={11} /> },
          ].map((cap, i) => (
            <div key={i} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs ${cap.active ? "bg-green-500/10 text-green-400 border border-green-500/20" : "bg-white/5 text-gray-500 border border-white/5"}`}>
              {cap.icon}{cap.label}
            </div>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        {[
          { label: "Total Agentes", value: agents.length, icon: <Bot size={16} />, color: "text-red-400" },
          { label: "Activos", value: activeCount, icon: <Activity size={16} />, color: "text-green-400" },
          { label: "Modelos Gratuitos", value: freeModels.length, icon: <Unlock size={16} />, color: "text-blue-400" },
          { label: "Modelos De Pago", value: paidModels.length, icon: <Star size={16} />, color: "text-yellow-400" },
        ].map(s => (
          <div key={s.label} className="glass-panel p-4">
            <div className={`flex items-center gap-2 mb-1 ${s.color}`}>{s.icon}<span className="text-xs">{s.label}</span></div>
            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* OpenRouter Panel */}
      <OpenRouterPanel status={openRouterStatus} onStatusRefresh={loadData} />

      {/* TokenRa Panel */}
      <TokenRaPanel status={tokenRaStatus} onStatusRefresh={loadData} />

      {/* Jev Decision Layer */}
      <JevPanel status={jevStatus} onStatusRefresh={loadData} />

      {/* Agents Grid */}
      {agents.length === 0 ? (
        <div className="glass-panel p-16 text-center">
          <div className="w-20 h-20 bg-white/5 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Bot className="w-10 h-10 text-gray-600" />
          </div>
          <p className="text-gray-400 mb-1 font-medium">No hay agentes creados</p>
          <p className="text-gray-600 text-sm mb-5">Crea tu primer agente IA personalizado y poderoso</p>
          <button onClick={() => setShowCreate(true)} className="btn-primary inline-flex items-center gap-2">
            <Plus size={16} />Crear primer agente
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {agents.map(agent => (
            <AgentCard
              key={agent.id}
              agent={agent}
              categories={categories}
              formats={formats}
              onEdit={setEditingAgent}
              onTest={setTestingAgent}
              onToggle={handleToggle}
              onDelete={handleDelete}
              onEmbed={setEmbedAgent}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      {showCreate && (
        <AgentModal
          agent={DEFAULT_AGENT}
          categories={categories}
          formats={formats}
          skills={skills}
          freeModels={freeModels}
          paidModels={paidModels}
          onSave={handleCreate}
          onClose={() => setShowCreate(false)}
          isNew
        />
      )}

      {editingAgent && (
        <AgentModal
          agent={editingAgent}
          categories={categories}
          formats={formats}
          skills={skills}
          freeModels={freeModels}
          paidModels={paidModels}
          onSave={handleUpdate}
          onClose={() => setEditingAgent(null)}
          isNew={false}
        />
      )}

      {testingAgent && (
        <TestModal agent={testingAgent} onClose={() => setTestingAgent(null)} />
      )}

      {embedAgent && (
        <EmbedCodeModal agent={embedAgent} onClose={() => setEmbedAgent(null)} />
      )}

      {showMainAgent && (
        <MainAgentModal
          freeModels={freeModels}
          paidModels={paidModels}
          skills={skills}
          onClose={() => setShowMainAgent(false)}
        />
      )}
    </div>
  );
}
